import { Fragment, useCallback, useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { confirmDialog } from "../lib/uiDialogs";
import {
  BookOpen,
  Plus,
  Pencil,
  Trash,
  MagnifyingGlass,
  Translate,
  ArrowsLeftRight,
  Buildings,
  Tag,
  SpinnerGap,
  SeedlingIcon,
  Database,
  X,
  Check,
  ArrowsClockwise,
  ClockCounterClockwise,
  VideoCamera as Video,
  Sparkle,
  Heart,
  TextAa,
  MagicWand,
  CaretDown,
  Warning,
} from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { useLang } from "../contexts/LanguageContext";
import { canEditContent } from "../utils/permissions";
import {
  SourceVideosPopover,
  DuplicateReviewModal,
  // Tâche 1161 — Majuscules en masse, avec aperçu et cases à cocher.
  BulkCaseFixModal,
  // Tâche 1163 — Avertissement de doublon pendant la saisie.
  SimilarTermWarning,
  // Tâche 1219 — Écran « Nettoyer les alias » (source des faux doublons).
  AliasCleanupModal,
  // Tâche 1220 — Journal des rejets de doublons dans la fiche du terme.
  TermRejectionLog,
  // Tâche 1235 — Corbeille des alias retirés par la garde.
  TermAliasTrash,
} from "../components/dictionary";
import PendingTermsModal from "../components/dictionary/PendingTermsModal"; // Bogue 1200
import DictCleanupModal from "../components/dictionary/DictCleanupModal"; // Vincent B961
import CrossRefBackfillModal from "../components/dictionary/CrossRefBackfillModal"; // Tâche 1131
import TranslationPairsModal from "../components/dictionary/TranslationPairsModal"; // Tâche 1135
import ReformulationResultModal from "../components/dictionary/ReformulationResultModal"; // Tâche 1353
import { entryMatchesQuery } from "../lib/bilingualFilter"; // Tâche 1134
import CsvImportModal from "../components/CsvImportModal"; // Vincent B1011
import ImportHistoryModal from "../components/ImportHistoryModal"; // Vincent B1012
import MisspellingCandidates from "../components/dictionary/MisspellingCandidates"; // Tâche 1265
import { toPlural, toSingular } from "../lib/tagInflect"; // Tâche 1264
import { useFocusFromURL } from "../hooks/useFocusFromURL";

import { API_BASE as API } from "../lib/apiBase";
// Eric 2026-07-30 (B609 enhancement, cohérence UX Dictionnaire) — Highlight
// partagé pour surligner les matches de recherche dans le terme + définition.
import { HighlightedText } from "../lib/highlight";
import { withScrollPreserved, markModified } from "../lib/preserveScroll";

// Tâche 1330 (Eric 2026-06-30) — Les deux langues sur la même ligne. La
// `value` reste l'identifiant technique envoyé à l'API : traduire une
// catégorie ne doit jamais changer ce qui est stocké ni filtré.
const CATEGORIES = [
  { value: "all", labels: ["Toutes", "All"] },
  { value: "produits", labels: ["Produits", "Products"] },
  { value: "conformite", labels: ["Conformité", "Compliance"] },
  { value: "frais", labels: ["Frais", "Fees"] },
  { value: "strategie", labels: ["Stratégie", "Strategy"] },
  { value: "performance", labels: ["Performance", "Performance"] },
  { value: "planification", labels: ["Planification", "Planning"] },
  { value: "retraite", labels: ["Retraite", "Retirement"] },
  { value: "juridique", labels: ["Juridique", "Legal"] },
  { value: "economie", labels: ["Économie", "Economy"] },
  { value: "general", labels: ["Général", "General"] },
];
const categorieLabel = (c, lang) => c.labels[lang === "en" ? 1 : 0];

// Vincent #231 (2026-06-12 + 2026-06-17 SPEC STRICK) — Onglets de la Base de connaissances en haut
// de la page, identiques à la page Connaissances, pour une présentation uniforme.
// Ordre IMPOSÉ par Vincent : "Q&R", "Scripts", "Procédures", "Concepts", "Objections", "Dictionnaire".
// Chaque onglet navigue vers /dashboard/knowledge?tab=X ; "Dictionnaire" est l'onglet actif ici.
const KB_TABS = [
  { value: "qa", labels: ["Q&R", "Q&A"] },
  { value: "script", labels: ["Scripts de vente", "Sales scripts"] },
  { value: "procedure", labels: ["Procédures", "Procedures"] },
  { value: "concept", labels: ["Concepts", "Concepts"] },
  { value: "objection", labels: ["Objections", "Objections"] },
];

export default function JargonPage() {
  const { user } = useAuth();
  // Droits d'édition : calculés dès le départ, car des hooks plus bas s'en
  // servent (une déclaration tardive provoquait un écran d'erreur complet).
  const isAdmin = canEditContent(user);
  const { lang, t, tr } = useLang();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Vincent B590.b (Eric 2026-07-29) — Focus item dictionary via ?focus=<id>
  const [search, setSearch] = useState("");
  // Vincent #232 (2026-06-12) — MULTI-sélection de catégories pour le filtre.
  // [] = toutes. Le filtrage est fait côté client (entries chargées en entier).
  const [selectedCategories, setSelectedCategories] = useState([]);
  // Vincent #31 — Filtre par tag (cliquable depuis une chip ou filter bar).
  const [selectedTag, setSelectedTag] = useState(null);

  // Vincent B685 (Eric 2026-02-XX) — Migration TanStack Query :
  // Le chargement de /api/jargon passe par useQuery (cache partagé,
  // background refetch, invalidation ciblée sur mutation).
  const {
    data: entries = [],
    isPending: loading,
    refetch: refetchEntries,
  } = useQuery({
    queryKey: ["jargon", "list", selectedTag ?? null],
    queryFn: async () => {
      const params = {};
      if (selectedTag) params.tag = selectedTag;
      try {
        const { data } = await axios.get(`${API}/api/jargon`, {
          params,
          withCredentials: true,
        });
        return data;
      } catch (err) {
        toast.error(tr("Erreur de chargement du dictionnaire", "Could not load the dictionary"));
        throw err;
      }
    },
    staleTime: 60_000,
  });
  // Vincent B1106 (Eric 2026-09-07) — Après une mutation, un simple `refetch()`
  // ne rafraîchissait QUE la clé active (`["jargon","list",<tag courant>]`).
  // Toutes les autres variantes (autre filtre par tag) restaient en cache
  // pendant `staleTime` (60 s) : quitter la page puis revenir dans la minute
  // réaffichait l'ANCIENNE valeur, donnant l'impression que la modification
  // du terme n'avait pas été enregistrée. On invalide donc TOUTE la famille
  // `["jargon", …]`, ce qui refetch la clé active et marque les autres périmées.
  // Bogue 1192 (Éric 2026-06-25) — Même règle que pour les tags : après une
  // action sur un terme, la liste ne doit pas remonter en haut. Toutes les
  // mutations de cette page passent par `loadEntries`.
  const loadEntries = useCallback(async () => {
    await withScrollPreserved(async () => {
      await queryClient.invalidateQueries({ queryKey: ["jargon"] });
      await refetchEntries();
    });
  }, [queryClient, refetchEntries]);
  const refreshPendingCount = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await axios.get(`${API}/api/jargon/pending`, { withCredentials: true });
      setPendingCount(data?.count || 0);
    } catch { /* silencieux : la bannière disparaît, rien de bloquant */ }
  }, [isAdmin]);

  useEffect(() => { refreshPendingCount(); }, [refreshPendingCount]);

  const refreshDuplicateWatch = useCallback(async () => {
    try {
      const { data } = await axios.post(`${API}/api/jargon/detect-duplicates`,
        {}, { withCredentials: true });
      const ids = new Set();
      (data.duplicate_groups || []).forEach((g) => {
        if (g.primary?.id) ids.add(g.primary.id);
        (g.duplicates || []).forEach((d) => d.id && ids.add(d.id));
      });
      setDupWatch({
        count: data.count || 0,
        identical: data.identical_count || 0,
        ids,
      });
    } catch {
      setDupWatch({ count: 0, identical: 0, ids: new Set() });
    }
  }, []);

  useFocusFromURL([entries.length]);

  // Vincent B621 (Eric 2026-07-30 SPEC STRICK) — Quand on arrive via
  // `?focus=<term_id>` depuis « Mes favoris », les filtres actifs peuvent
  // masquer le terme ciblé (catégorie sélectionnée, recherche…) →
  // `useFocusFromURL` ne trouve pas l'élément dans le DOM et le highlight
  // échoue silencieusement. On force donc le reset des filtres UNE FOIS au
  // mount si l'URL contient `?focus=`. L'utilisateur peut ensuite ré-appliquer
  // ses filtres à sa guise.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.get("focus")) return;
    setSearch("");
    setSelectedCategories([]);
    setSelectedTag(null);
  }, []);
  // Vincent #31 — Suggestions tags utilisées dans l'org (autocomplete).
  const [tagSuggestions, setTagSuggestions] = useState([]);
  // Vincent B1011 (Eric 2026-02-26 SPEC STRICK PROD) — Modale d'import
  // CSV massif de termes du dictionnaire. Réservé aux admins.
  const [showJargonImportModal, setShowJargonImportModal] = useState(false);
  // Vincent B1012 (Eric 2026-02-26 SPEC STRICK PROD) — Modale historique
  // + rollback des imports CSV.
  const [showImportHistoryModal, setShowImportHistoryModal] = useState(false);
  const submitJargonImport = async (payload) => {
    const { data } = await axios.post(
      `${API}/api/jargon/import`,
      payload,
      { withCredentials: true },
    );
    // Après un import réussi, on recharge la liste + toast.
    if (data?.added > 0) toast.success(`${data.added} terme(s) importé(s).`);
    else if (data?.skipped_duplicates > 0) toast.info(`${data.skipped_duplicates} doublon(s) ignoré(s), 0 ajout.`);
    await loadEntries();
    return data;
  };
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // Vincent B1004 (Eric 2026-02-26 SPEC STRICK PROD) — Ref sur le
  // conteneur du formulaire d'édition + effet qui le déplace dans
  // le DOM sous la ligne du terme édité (au lieu de le laisser en
  // haut de la page où l'utilisateur doit scroller pour le voir).
  const jargonFormRef = useRef(null);
  // B1089 (Eric 2026-09-05) — Noeud d'accueil du formulaire d'édition,
  // rendu PAR React juste sous la ligne éditée. Le formulaire y est
  // téléporté (portal) au lieu d'être déplacé à la main dans le DOM.
  const [editHost, setEditHost] = useState(null);
  // Vincent B677 — Popover sources cliquable sur le badge d'enrichissement
  const [sourcePopover, setSourcePopover] = useState(null); // {entryId, term, rect}
  // Vincent B677 — Modale détection/fusion de doublons
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);
  // Tâche 1163 (Eric 2026-06-25) — Doublons AU FIL DE L'EAU : la détection ne
  // doit plus attendre un clic. Elle tourne à l'ouverture de l'écran et après
  // chaque ajout / fusion, et signale les termes concernés.
  const [dupWatch, setDupWatch] = useState({ count: 0, identical: 0, ids: new Set() });
  // Tâche 1163 — Ce que l'avertissement de saisie a trouvé, pour pouvoir
  // demander confirmation AVANT d'enregistrer un terme déjà présent.
  const [similarInfo, setSimilarInfo] = useState(null);
  // Vincent B961 (Eric 2026-02-14 SPEC STRICK PROD) — Modal Dictionary Cleanup.
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  // Tâche 1219 (Eric 2026-06-26) — Écran « Nettoyer les alias ».
  const [showAliasModal, setShowAliasModal] = useState(false);
  const [showCrossRefModal, setShowCrossRefModal] = useState(false);
  // Tâche 1135 — Équivalences FR/EN de l'organisation, chargées une fois et
  // utilisées par le filtre de recherche bilingue (tâche 1134).
  const [showPairsModal, setShowPairsModal] = useState(false);
  const [translationPairs, setTranslationPairs] = useState([]);
  const loadTranslationPairs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/jargon/translation-pairs`, {
        withCredentials: true,
      });
      setTranslationPairs(data?.pairs || []);
    } catch {
      setTranslationPairs([]);
    }
  }, []);
  useEffect(() => {
    loadTranslationPairs();
  }, [loadTranslationPairs]);
  // Tâche 1133 — Copie d'un terme vers une organisation dont l'utilisateur
  // est membre. Les liens vidéos restent dans l'organisation d'origine.
  // `user.memberships` est VIDE pour les fondateurs (ils accèdent à toutes les
  // organisations sans enregistrement d'adhésion) : la liste doit venir de
  // /api/organizations/my, sinon le bouton reste invisible pour Eric.
  const [otherOrgs, setOtherOrgs] = useState([]);
  const [copyTarget, setCopyTarget] = useState(null);
  const [copyOrgId, setCopyOrgId] = useState("");
  const [copying, setCopying] = useState(false);
  useEffect(() => {
    axios
      .get(`${API}/api/organizations/my`, { withCredentials: true })
      .then((r) => setOtherOrgs((r.data?.organizations || []).filter((o) => o.id !== user?.org_id)))
      .catch(() => setOtherOrgs([]));
  }, [user?.org_id]);

  const openCopyDialog = (entry) => {
    setCopyTarget(entry);
    // Volontairement VIDE : une organisation pré-sélectionnée fait qu'un clic
    // trop rapide sur « Copier » envoie les termes au mauvais endroit. Le
    // choix doit être explicite.
    setCopyOrgId("");
  };

  // Tâche 1137 — Copie en lot. `copyTarget === "bulk"` réutilise le même
  // dialogue de choix d'organisation que la copie unitaire.
  const [bulkIds, setBulkIds] = useState(new Set());
  const toggleBulk = (id) => {
    setBulkIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const confirmCopy = async () => {
    if (!copyTarget || !copyOrgId) return;
    setCopying(true);
    const orgName = otherOrgs.find((o) => o.id === copyOrgId)?.name || copyOrgId;
    try {
      if (copyTarget === "bulk") {
        const { data } = await axios.post(
          `${API}/api/jargon/copy-to-org/bulk`,
          { ids: [...bulkIds], org_id: copyOrgId },
          { withCredentials: true },
        );
        toast.success(
          `${data.copied_count} terme(s) copié(s) vers ${orgName}` +
            (data.skipped_count ? ` — ${data.skipped_count} déjà présent(s), ignoré(s)` : "."),
        );
        setBulkIds(new Set());
      } else {
        await axios.post(
          `${API}/api/jargon/${copyTarget.id}/copy-to-org`,
          { org_id: copyOrgId },
          { withCredentials: true },
        );
        toast.success(`« ${copyTarget.term} » copié vers ${orgName}.`);
      }
      setCopyTarget(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Erreur de copie", "Copy failed"));
    } finally {
      setCopying(false);
    }
  };
  const [seeding, setSeeding] = useState(false);
  // Vincent #32 — État modal historique.
  const [historyEntry, setHistoryEntry] = useState(null);
  const [historyVersions, setHistoryVersions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // Vincent 2026-06-03 (Eric — items 23, 24, 25) — Tri (alpha A→Z, Z→A, récent),
  // multi-catégorie via tableau, suppression du champ redondant.
  const [sortBy, setSortBy] = useState("alpha");
  const [formData, setFormData] = useState({
    term: "",
    definition: "",
    aliases: "",
    categories: ["general"],
    // Vincent #31 — Tags libres : array de chaînes en lowercase.
    tags: [],
    // Tâche 1264 (Eric 2026-06-28) — Langue du terme, renvoi vers son
    // équivalent français et nombre. Un terme anglais est un alias : il
    // s'affiche en italique, avec le renvoi cliquable.
    lang: "fr",
    fr_equivalent: "",
    // Tâche 1359 (Eric 2026-06-30) — Équivalent anglais d'un terme français.
    en_equivalent: "",
    number: "",
  });
  // Vincent #31 — Input courant pour ajouter un tag (avec Enter/virgule).
  const [tagInput, setTagInput] = useState("");

  // Vincent B546 (Eric 2026-07-27) — Système de favoris pour Dictionnaire.
  const [favoriteIds, setFavoriteIds] = useState(new Set());

  const loadFavorites = async () => {
    try {
      const { data } = await axios.get(`${API}/api/favorites`, { withCredentials: true });
      setFavoriteIds(new Set(data.filter(f => f.item_type === "dictionary_term").map(f => f.item_id)));
    } catch { /* silencieux — favoris optionnels */ }
  };

  const toggleFavorite = async (e, termId) => {
    e.stopPropagation();
    const wasFavorite = favoriteIds.has(termId);
    setFavoriteIds(prev => {
      const s = new Set(prev);
      if (wasFavorite) s.delete(termId); else s.add(termId);
      return s;
    });
    try {
      if (wasFavorite) {
        await axios.delete(`${API}/api/favorites/dictionary_term/${termId}`, { withCredentials: true, _allowRetry: true });
      } else {
        await axios.post(`${API}/api/favorites`, { type: "dictionary_term", item_id: termId }, { withCredentials: true, _allowRetry: true });
      }
      // Vincent B605 (2026-07-29) — Notifie les autres pages (Dashboard,
      // Explorateur) que la liste des favoris a changé.
      try { window.dispatchEvent(new CustomEvent("favorites:updated")); } catch (_) { /* silencieux */ }
    } catch (err) {
      setFavoriteIds(prev => {
        const s = new Set(prev);
        if (wasFavorite) s.add(termId); else s.delete(termId);
        return s;
      });
      const msg = err?.response?.data?.detail || err?._friendlyMessage || "Impossible de mettre à jour le favori. Réessayez.";
      toast.error(msg);
    }
  };

  // Vincent #231 (Eric 2026-06-17 SPEC STRICK) — Bandeau supérieur uniforme :
  // recherche + 3 filtres (vidéos, statut, tri) AVANT les onglets KB.
  const [videos, setVideos] = useState([]);
  const [filterVideo, setFilterVideo] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | validated | candidates
  const [videoJargonIds, setVideoJargonIds] = useState(null); // set of jargon term ids used in selected video, or null = no filter

  useEffect(() => {
    // Charge la liste des vidéos pour le filtre vidéos.
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/videos`, { withCredentials: true });
        const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
        setVideos(items);
      } catch {
        setVideos([]);
      }
    })();
  }, []);

  useEffect(() => {
    // Quand l'utilisateur sélectionne une vidéo, on charge la liste des termes
    // de jargon utilisés dans cette vidéo et on les utilise pour filtrer.
    if (!filterVideo) {
      setVideoJargonIds(null);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/videos/${filterVideo}/jargon-terms`, { withCredentials: true });
        const ids = (data?.terms || data?.items || data || []).map((t) => t.id || t.jargon_id || t.term).filter(Boolean);
        setVideoJargonIds(new Set(ids));
      } catch {
        setVideoJargonIds(new Set());
      }
    })();
  }, [filterVideo]);

  // Vincent B685 (Eric 2026-02-XX) — L'ancien `loadEntries` useCallback et son
  // useEffect ont été remplacés par le `useQuery` déclaré plus haut. `loadEntries`
  // reste exposé (= refetch de la query) pour rester compatible avec les callers
  // (mutations qui appellent loadEntries() après un create/update/delete).

  const loadTagSuggestions = async () => {
    try {
      const { data } = await axios.get(`${API}/api/jargon/tags`, {
        withCredentials: true,
      });
      setTagSuggestions(data.tags || []);
    } catch (err) {
      // Non-critique : autocomplete reste vide si erreur.
      setTagSuggestions([]);
    }
  };

  useEffect(() => {
    loadTagSuggestions();
  }, []);

  // Vincent B605 (Eric 2026-07-29 SPEC STRICK) — loadFavorites n'était JAMAIS
  // appelé au montage, donc la Set favoriteIds restait toujours vide. Le ♥ à
  // gauche d'un terme du Dictionnaire ne pouvait donc jamais apparaître rempli
  // (rouge) même si le terme était effectivement dans les favoris de
  // l'utilisateur (ajouté depuis le Tableau de bord ou une session
  // précédente). Fix : appel initial + écoute de l'événement global
  // `favorites:updated` émis par les autres pages (Dashboard, Explorateur,
  // Base de connaissances) pour re-sync le cœur en temps réel après une
  // modification externe.
  useEffect(() => {
    loadFavorites();
    const onFavUpdated = () => { loadFavorites(); };
    window.addEventListener("favorites:updated", onFavUpdated);
    return () => window.removeEventListener("favorites:updated", onFavUpdated);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Tâche 1163 (Eric 2026-06-25) — Un bandeau discret ne suffisait pas.
    // Deux cas, et surtout : on ne propose JAMAIS une action que le serveur
    // refusera. Un homonyme exact est bloqué côté API (garde anti-doublon) :
    // on emmène donc l'utilisateur sur la fiche existante au lieu de lui
    // promettre une création impossible.
    if (!editingId && similarInfo?.has_identical) {
      const twins = (similarInfo.matches || [])
        .filter((m) => m.relation === "identical");
      const existing = twins.map((m) => `« ${m.term} »`).join(", ");
      const ok = await confirmDialog(
        lang === "fr"
          ? `Ce terme existe déjà : ${existing}. Un second terme identique n'est pas autorisé — mieux vaut enrichir la fiche existante.`
          : `This term already exists: ${existing}. A second identical entry is not allowed — enrich the existing one instead.`,
        { confirmLabel: lang === "fr" ? "Ouvrir la fiche existante" : "Open existing entry",
          cancelLabel: lang === "fr" ? "Annuler" : "Cancel" }
      );
      if (!ok) return;
      const twin = entries.find((en) => en.id === twins[0]?.id);
      if (twin) {
        handleEdit(twin);
      } else {
        setSearch(twins[0]?.term || "");
      }
      return;
    }
    // Voisin très proche (terme emboîté) : l'API l'accepte, on demande juste
    // confirmation pour que le doublon ne parte pas par inadvertance.
    if (!editingId && similarInfo?.count > 0) {
      const near = (similarInfo.matches || [])
        .map((m) => `« ${m.term} »`).join(", ");
      const ok = await confirmDialog(
        lang === "fr"
          ? `Un terme très proche existe déjà : ${near}. Créer quand même un nouveau terme ?`
          : `A very close term already exists: ${near}. Create a new term anyway?`,
        { confirmLabel: lang === "fr" ? "Créer quand même" : "Create anyway" }
      );
      if (!ok) return;
    }
    // Vincent #31 — Si le user a tapé un tag sans valider, on l'ajoute.
    const pending = tagInput.trim().toLowerCase();
    const finalTags = pending && !(formData.tags || []).includes(pending)
      ? [...(formData.tags || []), pending]
      : [...(formData.tags || [])];
    const payload = {
      term: formData.term,
      definition: formData.definition,
      aliases: formData.aliases
        .split(",")
        .map((a) => a.trim())
        .filter(Boolean),
      categories: formData.categories,
      tags: finalTags,
      lang: formData.lang === "en" ? "en" : "fr",
      fr_equivalent: (formData.fr_equivalent || "").trim(),
      en_equivalent: (formData.en_equivalent || "").trim(),
      number: formData.number || "",
    };

    try {
      if (editingId) {
        await axios.put(`${API}/api/jargon/${editingId}`, payload, {
          withCredentials: true,
        });
        toast.success(tr("Terme mis à jour", "Term updated"));
      } else {
        await axios.post(`${API}/api/jargon`, payload, {
          withCredentials: true,
        });
        toast.success("Terme ajoute");
      }
      // Bogue 1194 — le terme modifié s'illumine une seconde après le
      // rechargement, pour que l'oeil le retrouve sans le chercher. À lire
      // AVANT `resetForm()`, qui remet `editingId` à null.
      if (editingId) markModified(`jargon-entry-${editingId}`);
      resetForm();
      loadEntries();
      loadTagSuggestions();
      // Tâche 1163 — Un renommage peut créer un doublon sans changer le
      // nombre de termes : on relance la veille explicitement.
      refreshDuplicateWatch();
    } catch (err) {
      toast.error(err.response?.data?.detail || tr("Erreur", "Error"));
    }
  };

  // Tâche 1160 (Eric 2026-06-17) — Correction des majuscules d'un terme.
  // L'appel n'accepte côté serveur QU'une correction de casse : le terme
  // lui-même n'est jamais réécrit.
  const [fixingCase, setFixingCase] = useState(null);
  // Tâche 1353 (Eric 2026-06-30) — Reformulation d'une définition empilée.
  const [reformulating, setReformulating] = useState(null);
  const [reformResult, setReformResult] = useState(null);
  const [showBulkCaseModal, setShowBulkCaseModal] = useState(false);
  // Tâche 1173 — Une seule ligne d'actions : tout ce qui relève de la
  // détection passe sous le menu « Plus d'actions ».
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  // Tâche 1172 — Groupe construit à la main depuis les cases cochées.
  const [manualMergeGroup, setManualMergeGroup] = useState(null);
  // Bogue 1198 — terme dont on veut voir UNIQUEMENT les doublons.
  const [focusDuplicateEntry, setFocusDuplicateEntry] = useState(null);
  // Bogue 1200 — file « à valider »
  const [pendingCount, setPendingCount] = useState(0);
  const [showPendingModal, setShowPendingModal] = useState(false);

  const openManualMerge = () => {
    const picked = entries.filter((e) => bulkIds.has(e.id));
    if (picked.length < 2) {
      toast.info(tr("Cochez au moins deux termes à fusionner", "Tick at least two terms to merge"));
      return;
    }
    // Le terme conservé par défaut est celui qui a le plus de sources : c'est
    // lui qui est le plus référencé ailleurs.
    const sorted = [...picked].sort(
      (a, b) =>
        ((b.source_video_ids || []).length + (b.source_text_doc_ids || []).length)
        - ((a.source_video_ids || []).length + (a.source_text_doc_ids || []).length),
    );
    setManualMergeGroup({
      primary: sorted[0],
      duplicates: sorted.slice(1),
      confidence: 1,
      reason: "sélection manuelle", // i18n-donnee: raison PERSISTÉE puis relue telle quelle par la revue des fusions — la traduire à l'écriture ne traduirait rien pour le lecteur
      relation: "nested",
      needs_review: true,
    });
  };

  const fixCase = async (entry) => {
    setFixingCase(entry.id);
    try {
      const res = await axios.post(
        `${API}/api/jargon/${entry.id}/fix-case`,
        {},
        { withCredentials: true }
      );
      if (res.data?.changed) {
        toast.success(
          lang === "fr"
            ? `« ${res.data.previous_term} » → « ${res.data.term} »`
            : `"${res.data.previous_term}" → "${res.data.term}"`
        );
        markModified(`jargon-entry-${entry.id}`);
        loadEntries();
      } else {
        toast.info(
          lang === "fr" ? "La casse est déjà correcte" : "Capitalisation is already correct"
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || tr("Erreur", "Error"));
    } finally {
      setFixingCase(null);
    }
  };

  // Tâche 1353 — « Reformuler » : l'IA réécrit UNE définition unifiée au lieu
  // de l'empilement produit par les enrichissements successifs. Le terme
  // courant est nettoyé tout de suite ; les fragments qui appartiennent à un
  // autre terme restent des propositions à valider (choix d'Eric).
  const reformuler = async (entry) => {
    setReformulating(entry.id);
    try {
      const { data } = await axios.post(
        `${API}/api/jargon/${entry.id}/reformulate`,
        {},
        { withCredentials: true },
      );
      setReformResult(data);
      if (!data?.unchanged) {
        markModified(`jargon-entry-${entry.id}`);
        loadEntries();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || tr("Erreur", "Error"));
    } finally {
      setReformulating(null);
    }
  };

  const handleEdit = (entry) => {
    const cats = (entry.categories && entry.categories.length ? entry.categories : [entry.category || "general"]);
    setFormData({
      term: entry.term,
      definition: entry.definition,
      aliases: (entry.aliases || []).join(", "),
      categories: cats,
      tags: entry.tags || [],
      lang: entry.lang === "en" ? "en" : "fr",
      fr_equivalent: entry.fr_equivalent || "",
      en_equivalent: entry.en_equivalent || "",
      number: entry.number || "",
    });
    setTagInput("");
    setEditingId(entry.id);
    setShowForm(true);
  };

  // B1089 (Eric 2026-09-05) — CAUSE RACINE du bogue « Failed to execute
  // 'removeChild' on 'Node' » signalé à la sauvegarde d'un terme : la version
  // B1004 déplaçait le noeud DOM du formulaire (insertBefore) dans la liste
  // des termes. Au rechargement de la liste après sauvegarde, React tentait
  // de retirer des enfants qui n'étaient plus là où son arbre virtuel les
  // croyait → exception, écran cassé, modification apparemment perdue.
  // Désormais le formulaire est TÉLÉPORTÉ par React (createPortal) dans un
  // noeud d'accueil que React rend lui-même sous la ligne éditée : même
  // résultat visuel, zéro manipulation de DOM à la main. Cet effet ne fait
  // plus que du défilement.
  // Tâche 1171 (Eric 2026-06-17) — Plus AUCUN défilement à l'ouverture du
  // formulaire : il est déjà rendu juste sous la ligne éditée, donc déjà sous
  // les yeux. Le `scrollIntoView` déplaçait la page et faisait perdre le mot
  // qu'on venait de cliquer.

  const handleDelete = async (entryId) => {
    const ok = await confirmDialog(tr("Supprimer ce terme ?", "Delete this term?"), { danger: true, confirmLabel: tr("Supprimer", "Delete") });
    if (!ok) return;
    try {
      await axios.delete(`${API}/api/jargon/${entryId}`, {
        withCredentials: true,
      });
      toast.success(tr("Terme supprimé", "Term deleted"));
      loadEntries();
      loadTagSuggestions();
    } catch (err) {
      toast.error(tr("Erreur de suppression", "Deletion failed"));
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const { data } = await axios.post(`${API}/api/jargon/seed`, {}, {
        withCredentials: true,
      });
      toast.success(data.message);
      loadEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || tr("Erreur", "Error"));
    } finally {
      setSeeding(false);
    }
  };

  // Eric (2026-06-14 — LOT I) — Mapping AUTO Jargon ↔ Transcripts en background.
  const [mappingJobRunning, setMappingJobRunning] = useState(false);

  // Eric (2026-06-14 — LOT K) — Détection AUTO du jargon manquant.
  const [candidates, setCandidates] = useState([]);
  const [scanRunning, setScanRunning] = useState(false);
  const [showCandidates, setShowCandidates] = useState(false);

  const loadCandidates = async () => {
    try {
      const { data } = await axios.get(`${API}/api/jargon/candidates?status=pending`, { withCredentials: true });
      setCandidates(data?.items || []);
    } catch (_) { /* silent */ }
  };

  // Auto-load une fois au montage (badge avec compteur)
  useEffect(() => { loadCandidates(); }, []);
  // Tâche 1163 — La veille se relance quand la liste change (ajout manuel,
  // enrichissement IA, fusion) : le doublon se signale tout seul.
  useEffect(() => {
    if (!isAdmin) return;
    refreshDuplicateWatch();
  }, [isAdmin, entries.length, refreshDuplicateWatch]);

  // Tâche 1178 (Eric 2026-06-25) — Compteur de termes refusés affiché sur le
  // bouton. Le même appel que l'écran dédié (`include_rejected=true`), filtré
  // côté client : aucun nouvel endpoint pour un simple nombre.
  const [rejectedCount, setRejectedCount] = useState(0);
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    axios
      .get(`${API}/api/jargon?include_rejected=true`, { withCredentials: true })
      .then(({ data }) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setRejectedCount(list.filter((e) => e.status === "rejected").length);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isAdmin, entries.length]);

  const handleScanMissing = async () => {
    if (scanRunning) return;
    const ok = await confirmDialog(
      "Lancer la détection automatique du jargon manquant ? Knoria va scanner TOUS les transcripts, détecter les termes techniques absents du dictionnaire et (si une clé OpenAI est configurée) proposer une définition pour chacun. Durée : 1-3 min.",
      { confirmLabel: "Lancer la détection" },
    );
    if (!ok) return;
    setScanRunning(true);
    try {
      const start = await axios.post(`${API}/api/jargon/scan-missing?background=1`, {}, { withCredentials: true });
      const jobId = start.data?.job_id;
      if (!jobId) { toast.error(tr("Job non créé", "Job not created")); return; }
      toast.info(tr("Scan jargon manquant lancé en arrière-plan…", "Missing-jargon scan started in the background…"), { duration: 3500 });
      const t0 = Date.now();
      while (Date.now() - t0 < 600000) {
        await new Promise(r => setTimeout(r, 3000));
        const { data } = await axios.get(`${API}/api/agent/jobs/${jobId}`, { withCredentials: true });
        if (data.status === "done") {
          const r = data.result || {};
          toast.success(`✅ Scan terminé : ${r.candidates_found || 0} candidat(s) détecté(s) sur ${r.transcripts_scanned || 0} transcript(s). ${r.ai_enriched || 0} enrichi(s) par IA.`, { duration: 9000 });
          await loadCandidates();
          setShowCandidates(true);
          return;
        }
        if (data.status === "error") { toast.error(`Erreur : ${data.error || "inconnue"}`); return; }
      }
      toast.warning(tr("Délai dépassé — le job continue peut-être.", "Timed out — the job may still be running."));
    } catch (err) {
      toast.error(err.response?.data?.detail || tr("Erreur lancement scan", "Could not start the scan"));
    } finally {
      setScanRunning(false);
    }
  };

  const handleAcceptCandidate = async (c) => {
    try {
      const r = await axios.post(`${API}/api/jargon/candidates/${c.id}/accept`, {}, { withCredentials: true });
      if (r.data?.status === "promoted" || r.data?.status === "merged_existing") {
        toast.success(`✅ « ${c.term} » ajouté au dictionnaire.`);
        setCandidates(prev => prev.filter(x => x.id !== c.id));
        loadEntries();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || tr("Erreur acceptation", "Could not accept"));
    }
  };

  const handleRejectCandidate = async (c) => {
    const ok = await confirmDialog(
      `Rejeter définitivement « ${c.term} » ? Il ne sera plus suggéré.`,
      { danger: true, confirmLabel: "Rejeter" },
    );
    if (!ok) return;
    try {
      await axios.post(`${API}/api/jargon/candidates/${c.id}/reject`, {}, { withCredentials: true });
      toast.success(`« ${c.term} » rejeté.`);
      setCandidates(prev => prev.filter(x => x.id !== c.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || tr("Erreur rejet", "Could not reject"));
    }
  };

  // Tâche 1176 (Eric 2026-06-25) — Traitement en bloc des candidats détectés.
  // Valider 40 termes un par un est intenable : on coche, puis on tranche.
  const [selectedCandidates, setSelectedCandidates] = useState(new Set());
  const [bulkCandidateBusy, setBulkCandidateBusy] = useState(false);

  const toggleCandidate = (id) => {
    setSelectedCandidates((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAllCandidates = () => {
    setSelectedCandidates((prev) =>
      prev.size === candidates.length ? new Set() : new Set(candidates.map((c) => c.id)));
  };

  const handleBulkCandidates = async (action) => {
    const chosen = candidates.filter((c) => selectedCandidates.has(c.id));
    if (chosen.length === 0 || bulkCandidateBusy) return;
    if (action === "reject") {
      const ok = await confirmDialog(
        `Rejeter définitivement ${chosen.length} candidat(s) ? Ils ne seront plus suggérés.`,
        { danger: true, confirmLabel: `Rejeter ${chosen.length}` },
      );
      if (!ok) return;
    }
    setBulkCandidateBusy(true);
    let done = 0;
    const failed = [];
    // Séquentiel : le serveur déduplique à l'acceptation (fusion avec un terme
    // existant), en parallèle deux candidats proches créeraient un doublon.
    for (const c of chosen) {
      try {
        await axios.post(
          `${API}/api/jargon/candidates/${c.id}/${action}`,
          {},
          { withCredentials: true },
        );
        done += 1;
      } catch (err) {
        failed.push(c.term);
      }
    }
    const ids = new Set(chosen.filter((c) => !failed.includes(c.term)).map((c) => c.id));
    setCandidates((prev) => prev.filter((x) => !ids.has(x.id)));
    setSelectedCandidates(new Set());
    setBulkCandidateBusy(false);
    if (action === "accept") {
      toast.success(`${done} terme(s) ajouté(s) au dictionnaire.`);
      loadEntries();
    } else {
      toast.success(`${done} candidat(s) rejeté(s).`);
    }
    if (failed.length) {
      toast.error(`Échec sur ${failed.length} : ${failed.slice(0, 3).join(", ")}${failed.length > 3 ? "…" : ""}`);
    }
  };

  const handleMapAll = async () => {
    if (mappingJobRunning) return;
    const ok = await confirmDialog(
      "Recalculer le mapping Jargon ↔ Transcripts pour toute l'organisation ? Le job tourne en arrière-plan (1-3 min selon le nombre de vidéos). Une fois terminé, l'onglet « Utilisations » sera instantané.",
      { confirmLabel: "Recalculer" },
    );
    if (!ok) return;
    setMappingJobRunning(true);
    try {
      const start = await axios.post(`${API}/api/jargon/map-all?background=1`, {}, { withCredentials: true });
      const jobId = start.data?.job_id;
      if (!jobId) {
        toast.error(tr("Job non créé", "Job not created"));
        return;
      }
      toast.info(tr("Mapping lancé en arrière-plan…", "Mapping started in the background…"), { duration: 4000 });
      const t0 = Date.now();
      while (Date.now() - t0 < 600000) {  // 10 min max
        await new Promise(r => setTimeout(r, 3000));
        const { data } = await axios.get(`${API}/api/agent/jobs/${jobId}`, { withCredentials: true });
        if (data.status === "done") {
          const r = data.result || {};
          toast.success(`✅ Mapping terminé : ${r.links_created || 0} lien(s) sur ${r.entries_processed || 0} terme(s) × ${r.transcripts_scanned || 0} transcript(s).`, { duration: 9000 });
          return;
        }
        if (data.status === "error") {
          toast.error(`Erreur mapping : ${data.error || "inconnue"}`);
          return;
        }
      }
      toast.warning(tr("Délai dépassé — le job continue peut-être en arrière-plan.", "Timed out — the job may still be running in the background."));
    } catch (err) {
      toast.error(err.response?.data?.detail || tr("Erreur lancement mapping", "Could not start the mapping"));
    } finally {
      setMappingJobRunning(false);
    }
  };

  const resetForm = () => {
    setSimilarInfo(null);
    setFormData({ term: "", definition: "", aliases: "", categories: ["general"], tags: [],
      lang: "fr", fr_equivalent: "", en_equivalent: "", number: "" });
    setTagInput("");
    setEditingId(null);
    setShowForm(false);
  };

  const toggleFormCategory = (cat) => {
    setFormData((prev) => {
      const cur = prev.categories || [];
      const next = cur.includes(cat) ? cur.filter((c) => c !== cat) : [...cur, cat];
      return { ...prev, categories: next.length ? next : ["general"] };
    });
  };

  // Vincent #31 — Ajouter un tag (validation : Enter, virgule, ou blur).
  const addTag = (raw) => {
    const normalized = (raw || "").trim().toLowerCase().slice(0, 40);
    if (!normalized) return;
    setFormData((prev) => {
      if ((prev.tags || []).includes(normalized)) return prev;
      if ((prev.tags || []).length >= 20) {
        toast.error(tr("Maximum 20 tags par terme", "20 tags per term maximum"));
        return prev;
      }
      return { ...prev, tags: [...(prev.tags || []), normalized] };
    });
    setTagInput("");
  };

  const removeTag = (tag) => {
    setFormData((prev) => ({ ...prev, tags: (prev.tags || []).filter((t) => t !== tag) }));
  };

  const handleTagKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(tagInput);
    } else if (e.key === "Backspace" && !tagInput && (formData.tags || []).length > 0) {
      // Remove last tag on backspace when input is empty.
      const last = formData.tags[formData.tags.length - 1];
      removeTag(last);
    }
  };

  // Vincent #32 — Ouvre modal historique.
  const openHistory = async (entry) => {
    setHistoryEntry(entry);
    setHistoryLoading(true);
    setHistoryVersions([]);
    try {
      const { data } = await axios.get(`${API}/api/jargon/${entry.id}/history`, {
        withCredentials: true,
      });
      setHistoryVersions(data.versions || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Historique indisponible");
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeHistory = () => {
    setHistoryEntry(null);
    setHistoryVersions([]);
  };

  // Vincent #266 batch FLHQ (Eric 2026-02-06) — Ouvre modal "Vidéos d'apparition"
  // pour le terme courant. Affiche pour chaque vidéo : titre, nombre d'occurrences,
  // et jusqu'à 3 extraits de contexte avec le terme surligné.
  const [usageEntry, setUsageEntry] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);

  const openUsage = async (entry) => {
    setUsageEntry(entry);
    setUsageLoading(true);
    setUsageData(null);
    try {
      const { data } = await axios.get(`${API}/api/jargon/${entry.id}/usage`, {
        withCredentials: true,
      });
      setUsageData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Recherche d'usage indisponible");
    } finally {
      setUsageLoading(false);
    }
  };

  const closeUsage = () => {
    setUsageEntry(null);
    setUsageData(null);
  };

  // Vincent #32 — Restore une version.
  const handleRestore = async (version) => {
    if (!historyEntry) return;
    const ok = await confirmDialog(
      `Restaurer la version ${version} ? Une nouvelle version sera créée pour traçabilité.`,
      { confirmLabel: "Restaurer" },
    );
    if (!ok) return;
    try {
      await axios.post(`${API}/api/jargon/${historyEntry.id}/restore/${version}`, {}, {
        withCredentials: true,
      });
      toast.success(`Version ${version} restaurée`);
      closeHistory();
      loadEntries();
      loadTagSuggestions();
    } catch (err) {
      toast.error(err.response?.data?.detail || tr("Erreur de restauration", "Restore failed"));
    }
  };

  // Vincent 2026-06-03 — Filtre + tri locaux (item 20/23).
  // Vincent #232 (2026-06-12) — + filtre MULTI-catégories côté client.
  // Vincent #231 (Eric 2026-06-17 SPEC STRICK) — + filtres vidéos & statut.
  const filtered = entries
    .filter((e) => {
      // Filtre par catégories multi.
      if (selectedCategories.length === 0) return true;
      const cats = (e.categories && e.categories.length ? e.categories : [e.category || "general"]).filter(Boolean);
      return cats.some((c) => selectedCategories.includes(c));
    })
    .filter((e) => {
      // Filtre recherche — tâche 1134 : les équivalences FR/EN s'appliquent
      // ici aussi, taper « TFSA » doit remonter « CELI ».
      if (!search) return true;
      return entryMatchesQuery(e, search, translationPairs);
    })
    .filter((e) => {
      // Filtre par vidéo (Vincent #231).
      if (!filterVideo || videoJargonIds === null) return true;
      return videoJargonIds.has(e.id) || videoJargonIds.has(e.term);
    })
    .filter((e) => {
      // Filtre par statut (Vincent #231).
      // Les `entries` sont toujours des termes validés ; "candidates" est une
      // collection séparée affichée dans le panneau ad-hoc plus bas.
      if (statusFilter === "candidates") return false; // masque les entries validés
      return true;
    })
    .sort((a, b) => {
      if (sortBy === "alpha") return a.term.localeCompare(b.term, "fr", { sensitivity: "base" });
      if (sortBy === "alpha_desc") return b.term.localeCompare(a.term, "fr", { sensitivity: "base" });
      if (sortBy === "recent") return (b.created_at || "").localeCompare(a.created_at || "");
      return 0;
    });

  // Vincent #25/26 (Eric 2026-06-04) → Eric 2026-06-25 (iter117 B2) — migré
  // vers le helper canonique canEditContent (utils/permissions.js) — single
  // source of truth aligné avec backend require_editor (core/auth.py).
  // `isAdmin` est déclaré EN TÊTE de composant (voir plus haut) : un hook le
  // lisait avant son initialisation et la page ne s'affichait plus du tout.

  return (
    <div className="animate-fade-in max-w-5xl mx-auto">
      {/* Vincent #231 (Eric 2026-06-17 SPEC STRICK) — Refonte ordre :
          1) Titre + actions admin
          2) Bandeau supérieur uniforme (recherche + 3 filtres : vidéos, statut, tri)
          3) Ligne d'onglets KB ("Q&R", "Scripts", "Procédures", "Concepts", "Objections", "Dictionnaire")
          4) Catégories propres au Dictionnaire
          5) Liste des termes */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1
            className="text-2xl font-semibold tracking-tight font-['Outfit']"
            data-testid="jargon-page-title"
          >
            {tr("Dictionnaire (jargon métier)", "Dictionary (industry jargon)")}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {tr(
              "Terminologie financière normalisée pour l\u2019extraction de connaissances",
              "Standardized financial terminology for knowledge extraction",
            )}
          </p>
        </div>
        {isAdmin && (
          <div className="flex gap-2 flex-wrap">
            {entries.length === 0 && (
              <Button
                variant="outline"
                onClick={handleSeed}
                disabled={seeding}
                data-testid="seed-jargon-btn"
              >
                {seeding ? (
                  <SpinnerGap size={16} className="animate-spin mr-2" />
                ) : (
                  <Database size={16} className="mr-2" />
                )}
                {tr("Termes par défaut", "Default terms")}
              </Button>
            )}
            {/* Vincent #357 (Eric 2026-06-19 SPEC STRICK) — Boutons "Données à
                jour" + "Corbeille" en haut à droite, comme demandé pour
                cohérence avec les autres onglets de la Base de connaissances. */}
            <Button
              variant="outline"
              onClick={() => loadEntries()}
              data-testid="jargon-refresh-btn"
              className="border-teal-300 text-teal-700 hover:bg-teal-50"
              title={tr("Rafraîchir le dictionnaire à partir du serveur", "Refresh the dictionary from the server")}
            >
              <Database size={16} className="mr-2" />
              {tr("Données à jour", "Data up to date")}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/dashboard/admin/jargon/trash")}
              data-testid="jargon-trash-btn"
              className="border-zinc-300 text-zinc-700 hover:bg-zinc-50"
              title={tr("Voir la corbeille du dictionnaire — les termes supprimés y restent 30 jours", "Open the dictionary bin — deleted terms stay there for 30 days")}
            >
              {/* Tâche 1139 — Icône, pas d'emoji. */}
              <Trash size={16} className="mr-2" />
              {tr("Corbeille", "Bin")}
            </Button>
            {/* Vincent B967 — L'écran des termes refusés existait depuis
                février, à l'adresse /dashboard/admin/dict-rejected, mais
                AUCUN bouton n'y menait : une page sans porte d'entrée est
                une page qui n'existe pas. Signalé par Eric le 2026-06-17. */}
            <Button
              variant="outline"
              onClick={() => navigate("/dashboard/admin/dict-rejected")}
              data-testid="jargon-rejected-btn"
              className="border-amber-300 text-amber-800 hover:bg-amber-50"
              title={tr("Voir les termes marqués « non pertinents » et en restaurer", "See terms marked “not relevant” and restore some")}
            >
              <ArrowsClockwise size={16} className="mr-2" />
              {tr("Termes refusés", "Rejected terms")}
              {/* Tâche 1178 (Eric 2026-06-25) — Compteur : sans lui, il faut
                  ouvrir l'écran pour savoir s'il y a du ménage à faire. */}
              {rejectedCount > 0 && (
                <span
                  className="ml-2 px-1.5 py-0.5 rounded-full bg-amber-200 text-amber-900 text-[10px] font-bold tabular-nums"
                  data-testid="jargon-rejected-count"
                >
                  {rejectedCount}
                </span>
              )}
            </Button>
            {/* Vincent B1011 (Eric 2026-02-26 SPEC STRICK PROD) — Bouton
                d'import CSV massif de termes. Réservé aux admins. Ouvre
                une modale réutilisable (CsvImportModal) qui gère fichier,
                preview et affichage du résumé. */}
            <Button
              variant="outline"
              onClick={() => setShowJargonImportModal(true)}
              data-testid="jargon-csv-import-btn"
              className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
              title={tr("Importer un CSV de termes dans le dictionnaire", "Import a CSV of terms into the dictionary")}
            >
              📥 {tr("Importer CSV", "Import CSV")}
            </Button>
            {/* Vincent B1012 (Eric 2026-02-26 SPEC STRICK PROD) — Bouton
                historique + rollback des imports (jargon uniquement ici). */}
            <Button
              variant="outline"
              onClick={() => setShowImportHistoryModal(true)}
              data-testid="jargon-import-history-btn"
              className="border-zinc-300 text-zinc-700 hover:bg-zinc-50"
              title={tr("Voir l'historique des imports CSV/XLSX et annuler un import en 1 clic", "See the CSV/XLSX import history and undo an import in 1 click")}
            >
              📜 {tr("Historique imports", "Import history")}
            </Button>
            {/* Vincent #350 (Eric 2026-06-18 SPEC STRICK ULTRA) — Les 3 boutons
                "Détecter jargon manquant", "Recalculer mapping vidéos" et
                "Ajouter un terme" ont été DÉPLACÉS sous les onglets KB.
                Conservés ici : "Termes par défaut" (seed). */}
          </div>
        )}
      </div>

      {/* Vincent #231 (Eric 2026-06-17 SPEC STRICK) — Bandeau supérieur uniforme
          identique aux autres onglets KB : recherche + 3 filtres (vidéos, statut, tri).
          Suit immédiatement le titre, AVANT la ligne d'onglets. */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tr("Rechercher un terme, une définition ou un alias…", "Search a term, a definition or an alias…")}
            className="pl-10 h-11 rounded-sm"
            data-testid="jargon-search-input"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
            >
              <X size={16} />
            </button>
          )}
        </div>
        <select
          value={filterVideo}
          onChange={(e) => setFilterVideo(e.target.value)}
          className="h-11 px-3 border border-zinc-200 rounded-lg text-sm bg-white"
          data-testid="jargon-filter-video"
        >
          <option value="">{tr("Toutes les vidéos", "All videos")}</option>
          {videos.map((v) => (
            <option key={v.id} value={v.id}>
              {(v.title || "").substring(0, 40)}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-11 px-3 border border-zinc-200 rounded-lg text-sm bg-white"
          data-testid="jargon-status-filter"
        >
          <option value="all">{tr("Tous statuts", "All statuses")}</option>
          <option value="validated">{tr("Validés seulement", "Validated only")}</option>
          <option value="candidates">{tr("Candidats à valider", "Candidates to validate")}</option>
        </select>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="h-11 px-3 border border-zinc-200 rounded-lg text-sm bg-white"
          data-testid="jargon-sort-select-top"
        >
          <option value="alpha">A → Z (terme)</option>
          <option value="alpha_desc">Z → A (terme)</option>
          <option value="recent">{tr("↓ Récent d\u2019abord", "↓ Newest first")}</option>
        </select>
      </div>

      {/* Vincent #231 (Eric 2026-06-17 SPEC STRICK) — Ligne d'onglets KB
          ("Q&R", "Scripts", "Procédures", "Concepts", "Objections", "Dictionnaire"). */}
      <div className="mb-6">
        <div className="w-full sm:w-auto inline-flex flex-wrap gap-1 p-1 bg-zinc-100 rounded-sm" data-testid="jargon-kb-tabs">
          {KB_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => navigate(`/dashboard/knowledge?tab=${tab.value}`)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-sm text-zinc-700 hover:bg-white hover:shadow-sm transition-colors"
              data-testid={`jargon-kb-tab-${tab.value}`}
            >
              {tab.labels[lang === "en" ? 1 : 0]}
            </button>
          ))}
          <span
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-sm bg-white shadow-sm font-medium text-zinc-900"
            data-testid="jargon-kb-tab-dictionary"
          >
            <BookOpen size={16} />
            {tr("Dictionnaire", "Dictionary")}
          </span>
        </div>
      </div>

      {/* Vincent #350 (Eric 2026-06-18 SPEC STRICK ULTRA) — Boutons d'action du
          dictionnaire déplacés ICI : sous les onglets KB, AU-DESSUS des catégories,
          encadrés par 2 traits horizontaux. */}
      {isAdmin && (
        <>
          <hr className="border-zinc-200 mb-3" />
          {/* Tâche 1173 — Boutons compacts (`px-2.5`) et libellés courts pour
              que la barre tienne sur deux lignes. */}
          {/* Bogue 1200 (Éric 2026-06-25) — File « à valider » : les termes
              générés depuis une synthèse attendent une décision humaine
              (valider / fusionner / rejeter) avant d'entrer au dictionnaire.
              C'est là qu'on règle les doublons AVANT qu'ils polluent la
              liste. */}
          {isAdmin && pendingCount > 0 && (
            <button
              type="button"
              onClick={() => setShowPendingModal(true)}
              className="w-full mb-3 flex items-center gap-2 rounded-lg border border-teal-300 bg-teal-50 px-3 py-2 text-left text-xs text-teal-900 transition-colors hover:bg-teal-100"
              data-testid="pending-terms-banner"
            >
              <Warning size={16} className="flex-shrink-0" />
              <span className="font-semibold">
                {pendingCount}{" "}
                {pendingCount > 1
                  ? (lang === "fr" ? "termes à valider" : "terms awaiting validation")
                  : (lang === "fr" ? "terme à valider" : "term awaiting validation")}
              </span>
              <span className="ml-auto font-semibold underline">
                {lang === "fr" ? "Examiner" : "Review"}
              </span>
            </button>
          )}

          {/* Tâche 1163 — Doublons AU FIL DE L'EAU : signalés sans clic. */}
          {isAdmin && dupWatch.count > 0 && (
            <button
              type="button"
              onClick={() => setShowDuplicatesModal(true)}
              className="w-full mb-3 flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-left text-xs text-amber-900 transition-colors hover:bg-amber-100"
              data-testid="auto-duplicates-banner"
            >
              <Warning size={16} className="flex-shrink-0" />
              <span className="font-semibold">
                {dupWatch.count}{" "}
                {dupWatch.count > 1
                  ? (lang === "fr" ? "doublons potentiels détectés" : "potential duplicates found")
                  : (lang === "fr" ? "doublon potentiel détecté" : "potential duplicate found")}
              </span>
              {dupWatch.identical > 0 && (
                <span className="rounded bg-red-100 px-1.5 py-0.5 font-medium text-red-800">
                  {dupWatch.identical}{" "}
                  {lang === "fr" ? "à fusionner d'office" : "ready to merge"}
                </span>
              )}
              <span className="ml-auto underline">
                {lang === "fr" ? "Examiner" : "Review"}
              </span>
            </button>
          )}
          <div className="flex flex-wrap gap-1.5 mb-3 items-center [&_button]:px-2.5" data-testid="jargon-actions-row">
            <Button
              onClick={() => { resetForm(); setShowForm(true); }}
              className="bg-[#002FA7] hover:bg-[#002280]"
              data-testid="add-jargon-btn-bottom"
            >
              <Plus size={16} className="mr-1.5" /> {tr("Ajouter", "Add")}
            </Button>
            {/* Tâche 1173 (Eric 2026-06-17) — Les détections tiennent dans un
                menu : la barre garde une seule ligne, quelle que soit la
                largeur de l'écran. */}
            <DropdownMenu open={actionsMenuOpen} onOpenChange={setActionsMenuOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                  data-testid="jargon-more-actions-btn"
                >
                  {lang === "fr" ? "Plus d'actions" : "More actions"}
                  <CaretDown size={14} className="ml-1.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="w-auto max-w-[min(92vw,26rem)] p-1.5 flex flex-col items-stretch gap-1 [&_button]:w-full [&_button]:justify-start [&_button]:px-2.5"
                onClick={() => setActionsMenuOpen(false)}
                data-testid="jargon-more-actions-menu"
              >
            <Button
              variant="outline"
              onClick={handleScanMissing}
              disabled={scanRunning}
              data-testid="scan-missing-jargon-btn-bottom"
              className="border-amber-300 text-amber-700 hover:bg-amber-50 relative"
            >
              {scanRunning ? <SpinnerGap size={16} className="animate-spin mr-1.5" /> : <Sparkle size={16} className="mr-1.5" />}
              {scanRunning ? "Scan IA en cours…" : "Détecter jargon manquant"}
              {candidates.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                  {candidates.length}
                </span>
              )}
            </Button>
            <Button
              onClick={() => setShowDuplicatesModal(true)}
              variant="outline"
              className="border-amber-300 text-amber-900 hover:bg-amber-50"
              data-testid="detect-duplicates-btn"
              title={lang === "fr" ? "Détecte les termes en double par similarité et propose une fusion" : "Detect duplicate terms by similarity and offer merging"}
            >
              <MagnifyingGlass size={16} className="mr-1.5" />
              {lang === "fr" ? "Détecter les doublons" : "Detect duplicates"}
            </Button>
            {/* Tâche 1172 (Eric 2026-06-17) — Fusionner une sélection faite à
                la main : la détection ne rapproche pas forcément ces termes,
                et c'est précisément pourquoi Eric les choisit lui-même. */}
            <Button
              onClick={openManualMerge}
              disabled={bulkIds.size < 2}
              variant="outline"
              className="px-2.5 border-amber-300 text-amber-900 hover:bg-amber-50 disabled:opacity-40"
              data-testid="merge-selection-btn"
              title={bulkIds.size < 2
                ? tr("Cochez au moins deux termes dans la liste", "Tick at least two terms in the list")
                : tr(`Fusionner les ${bulkIds.size} termes cochés en un seul`, `Merge the ${bulkIds.size} ticked terms into one`)}
            >
              <ArrowsClockwise size={16} className="mr-1.5" />
              {lang === "fr" ? `Fusionner la sélection (${bulkIds.size})` : `Merge selection (${bulkIds.size})`}
            </Button>
            {/* Tâche 1135 (Eric 2026-06-09) — Table FR/EN modifiable
                depuis l'écran, sans déploiement. */}
            <Button
              onClick={() => setShowPairsModal(true)}
              variant="outline"
              className="border-blue-300 text-blue-800 hover:bg-blue-50"
              data-testid="translation-pairs-btn"
              title={lang === "fr"
                ? "Ajouter ou corriger les équivalences français/anglais utilisées par les doublons, les renvois et la recherche"
                : "Manage the French/English equivalences"}
            >
              <ArrowsLeftRight size={16} className="mr-1.5" />
              {lang === "fr" ? "Détecter équivalents FR/EN" : "FR/EN equivalences"}
            </Button>
            {/* Vincent B961 (Eric 2026-02-14 SPEC STRICK PROD) — Cleanup
                blacklist B960.d : preview des termes flaggés + soft-reject. */}
            <Button
              onClick={() => setShowCleanupModal(true)}
              variant="outline"
              className="border-rose-300 text-rose-800 hover:bg-rose-50"
              data-testid="dict-cleanup-btn"
              title={lang === "fr"
                ? "Détecte les termes non pertinents (mots outils, prénoms, verbes conjugués) via la blacklist B960.d et propose de les masquer"
                : "Detect irrelevant terms (filler words, first names, conjugated verbs) via the B960.d blacklist and offer to hide them"}
            >
              <MagnifyingGlass size={16} className="mr-1.5" />
              {lang === "fr" ? "Détecter termes non pertinents" : "Cleanup irrelevant terms"}
            </Button>
            {/* Tâche 1219 (Eric 2026-06-26) — Les faux doublons viennent des
                alias pollués : on les traite à la source, dans un écran qui
                dit POURQUOI chaque alias est douteux. */}
            <Button
              onClick={() => setShowAliasModal(true)}
              variant="outline"
              className="border-rose-300 text-rose-800 hover:bg-rose-50"
              data-testid="alias-cleanup-btn"
              title={tr("Repère les alias absurdes (identiques au titre, trop courts, titre d'un autre terme) qui font croire à des doublons, et permet de les désactiver sans les supprimer", "Spots nonsensical aliases (identical to the title, too short, another term's title) that look like duplicates, and lets you disable them without deleting")}
            >
              <MagnifyingGlass size={16} className="mr-1.5" />
              {lang === "fr" ? "Nettoyer les alias" : "Clean up aliases"}
            </Button>
            {isAdmin && (
              <Button
                onClick={async () => {
                  const ok = await confirmDialog(
                    lang === "fr"
                      ? "Enrichir le dictionnaire à partir des vidéos historiques ?\n\nCe bouton lance GPT-5.2 sur les 20 premières vidéos qui n'ont JAMAIS contribué au dictionnaire (uploadées avant B676 ou dont le pipeline n'incluait pas encore la génération dictionnaire).\n\n  • Coût : jusqu'à 20 appels GPT-5.2 (~2-8 min total)\n  • Chaque terme déjà présent sera ENRICHI (jamais dupliqué)\n\nContinuer ?"
                      : "Enrich dictionary from historical videos?",
                    { confirmLabel: lang === "fr" ? "Lancer" : "Run" },
                  );
                  if (!ok) return;
                  const toastId = toast.loading(
                    lang === "fr" ? "⚡ Lancement en cours…" : "⚡ Starting…",
                    { duration: Infinity },
                  );
                  try {
                    // Vincent B681 — POST retourne immédiatement avec un job_id.
                    // Poll GET status/{job_id} toutes les 2 s pour afficher la progression.
                    const { data: launch } = await axios.post(
                      `${API}/api/jargon/enrich-historical`,
                      { limit: 20 },
                      { withCredentials: true, timeout: 30000 },
                    );
                    if (launch.total === 0) {
                      toast.success(
                        lang === "fr"
                          ? "🎉 Dictionnaire déjà à jour — aucune vidéo historique à enrichir."
                          : "🎉 Dictionary up to date.",
                        { id: toastId, duration: 6000 },
                      );
                      return;
                    }
                    const jobId = launch.job_id;
                    // Poll loop with progress toast update
                    let done = false;
                    let attempts = 0;
                    const MAX_ATTEMPTS = 300; // 300 * 2s = 10 min max
                    while (!done && attempts < MAX_ATTEMPTS) {
                      await new Promise((r) => setTimeout(r, 2000));
                      attempts++;
                      try {
                        const { data: job } = await axios.get(
                          `${API}/api/jargon/enrich-historical/status/${jobId}`,
                          { withCredentials: true, timeout: 10000 },
                        );
                        if (job.status === "running") {
                          const cur = job.current_video_title
                            ? ` : « ${job.current_video_title.substring(0, 40)}${job.current_video_title.length > 40 ? "…" : ""} »`
                            : "";
                          toast.loading(
                            lang === "fr"
                              ? `📚 Traitement ${job.processed + 1}/${job.total}${cur}`
                              : `📚 Processing ${job.processed + 1}/${job.total}${cur}`,
                            { id: toastId, duration: Infinity },
                          );
                        } else if (job.status === "complete") {
                          done = true;
                          const vp = job.total - (job.failed || 0);
                          toast.success(
                            lang === "fr"
                              ? `⚡ ${vp} vidéo(s) traitée(s). +${job.terms_created} nouveau(x), ${job.terms_enriched} enrichi(s).`
                              : `⚡ ${vp} video(s) processed. +${job.terms_created} new, ${job.terms_enriched} enriched.`,
                            { id: toastId, duration: 10000 },
                          );
                          if (job.failed > 0) {
                            toast.warning(
                              lang === "fr"
                                ? `⚠️ ${job.failed} vidéo(s) sautée(s).`
                                : `⚠️ ${job.failed} video(s) skipped.`,
                              { duration: 8000 },
                            );
                          }
                          await loadEntries();
                        } else if (job.status === "failed") {
                          done = true;
                          toast.error(
                            (lang === "fr" ? "❌ Job échoué : " : "❌ Job failed: ") + (job.error || "unknown"),
                            { id: toastId, duration: 10000 },
                          );
                        }
                      } catch (pollErr) {
                        console.warn("[enrich-historical] poll failed:", pollErr);
                        // continue polling
                      }
                    }
                    if (!done) {
                      toast.warning(
                        lang === "fr"
                          ? "⏱️ Batch toujours en cours après 10 min. Rafraîchis la page pour voir le résultat final."
                          : "⏱️ Batch still running after 10 min. Refresh to see final result.",
                        { id: toastId, duration: 10000 },
                      );
                    }
                  } catch (err) {
                    toast.error(
                      (lang === "fr" ? "❌ Échec : " : "❌ Failed: ") +
                        (err.response?.data?.detail || err.message),
                      { id: toastId, duration: 10000 },
                    );
                  }
                }}
                variant="outline"
                className="border-violet-300 text-violet-800 hover:bg-violet-50"
                data-testid="enrich-historical-btn"
                title={lang === "fr" ? "Relance generate-dictionary sur les vidéos historiques qui n'ont jamais contribué au dictionnaire" : "Re-run generate-dictionary on historical videos"}
              >
                <Sparkle size={16} className="mr-1.5" />
                {lang === "fr" ? "Enrichir depuis vidéos historiques" : "Enrich from history"}
              </Button>
            )}
            {/* Tâche 1131 (Eric 2026-06-09) — Renvois EN/FR en masse pour tout
                le dictionnaire antérieur à B1130. */}
            <Button
              onClick={() => setShowCrossRefModal(true)}
              variant="outline"
              className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
              data-testid="cross-ref-backfill-btn"
              title={lang === "fr"
                ? "Crée les entrées de renvoi anglaises manquantes pour tout le dictionnaire, sans toucher aux titres français"
                : "Create the missing English cross-reference entries for the whole dictionary"}
            >
              <Translate size={16} className="mr-1.5" />
              {lang === "fr" ? "Détecter renvois manquants" : "Missing EN/FR cross-refs"}
            </Button>
            {entries.length > 0 && (
              <Button
                variant="outline"
                onClick={handleMapAll}
                disabled={mappingJobRunning}
                data-testid="map-jargon-btn-bottom"
                className="border-violet-300 text-violet-700 hover:bg-violet-50"
              >
                {mappingJobRunning ? <SpinnerGap size={16} className="animate-spin mr-1.5" /> : <Database size={16} className="mr-1.5" />}
                {mappingJobRunning ? "Mapping en cours…" : "MAJ vidéos concernées"}
              </Button>
            )}
            {/* Tâche 1161 (Eric 2026-06-17) — Rattraper d'un coup les titres
                hérités de l'ancienne normalisation en Title Case. */}
            <Button
              onClick={() => setShowBulkCaseModal(true)}
              variant="outline"
              className="border-violet-300 text-violet-700 hover:bg-violet-50"
              data-testid="bulk-case-btn"
              title={lang === "fr" ? "Corriger les majuscules de tout le dictionnaire (aperçu avant application)" : "Fix capitalisation across the dictionary (preview first)"}
            >
              <TextAa size={16} className="mr-1.5" />
              {lang === "fr" ? "Corriger majuscules" : "Bulk capitalisation"}
            </Button>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <hr className="border-zinc-200 mb-6" />
        </>
      )}

      {/* Vincent B1004 (Eric 2026-02-26 SPEC STRICK PROD) — Le formulaire
          d'édition d'un terme existant est déplacé INLINE sous la ligne
          du terme via React Portal (voir logique du placement plus bas).
          Le formulaire d'ajout d'un NOUVEAU terme (editingId === null)
          reste au sommet pour rester visible sans scroll après le clic
          « Ajouter un terme ». */}
      {showForm && isAdmin && ((formNode) => (
        editingId && editHost ? createPortal(formNode, editHost) : formNode
      ))(
        <div
          ref={jargonFormRef}
          className="bg-white border border-zinc-200 rounded-sm p-6 mb-6"
          data-testid="jargon-form"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium">
              {editingId ? "Modifier le terme" : "Nouveau terme"}
            </h3>
            <button
              onClick={resetForm}
              className="p-1 hover:bg-zinc-100 rounded"
              data-testid="close-form-btn"
            >
              <X size={18} />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-zinc-700 mb-1 block">
                  Terme
                </label>
                <Input
                  value={formData.term}
                  onChange={(e) =>
                    setFormData({ ...formData, term: e.target.value })
                  }
                  placeholder="Ex: REER"
                  required
                  data-testid="jargon-term-input"
                />
                {/* Tâche 1163 — Le doublon se prévient à la frappe, pas trois
                    semaines plus tard dans « Détecter les doublons ». */}
                <SimilarTermWarning
                  term={formData.term}
                  excludeId={editingId || ""}
                  onResult={setSimilarInfo}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 mb-1 block">
                  {tr("Catégories", "Categories")} <span className="text-zinc-400 font-normal">{tr("(multi-sélection)", "(multi-select)")}</span>
                </label>
                <div className="flex flex-wrap gap-1.5" data-testid="jargon-categories-multiselect">
                  {CATEGORIES.filter((c) => c.value !== "all").map((c) => {
                    const active = (formData.categories || []).includes(c.value);
                    return (
                      <button
                        key={c.value}
                        type="button"
                        onClick={() => toggleFormCategory(c.value)}
                        className={`px-2.5 py-1 text-xs rounded-sm border transition-colors ${
                          active
                            ? "bg-[#002FA7] text-white border-[#002FA7]"
                            : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"
                        }`}
                        data-testid={`form-category-${c.value}`}
                      >
                        {categorieLabel(c, lang)}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            {/* Tâche 1264 (Eric 2026-06-28) — Langue du terme et nombre. Les
                boutons « singulier » et « pluriel » sont collés : ce sont deux
                faces d'un même réglage, et ils réécrivent le terme saisi. */}
            <div className="flex flex-wrap items-end gap-4" data-testid="jargon-lang-number-row">
              <div>
                <label className="text-sm font-medium text-zinc-700 mb-1 block">
                  {tr("Langue du terme", "Term language")}
                </label>
                <span className="inline-flex" data-testid="jargon-lang-group">
                  {[["fr", "Fr"], ["en", "En"]].map(([value, label], i) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setFormData({ ...formData, lang: value })}
                      className={`px-3 py-1.5 text-xs font-bold border ${
                        i === 0 ? "rounded-l-sm" : "rounded-r-sm -ml-px"
                      } ${
                        (formData.lang || "fr") === value
                          ? "bg-[#002FA7] text-white border-[#002FA7]"
                          : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"
                      }`}
                      data-testid={`jargon-lang-${value}`}
                      title={value === "en"
                        ? "Terme anglais (alias) : affiché en italique, avec renvoi vers l'équivalent français"
                        : "Terme français"}
                    >
                      {label}
                    </button>
                  ))}
                </span>
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 mb-1 block">
                  Nombre
                </label>
                <span className="inline-flex" data-testid="jargon-number-group">
                  {[["singulier", "singulier", toSingular], ["pluriel", "pluriel", toPlural]]
                    .map(([value, label, apply], i) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          number: value,
                          term: apply(formData.term || ""),
                        })}
                        className={`px-3 py-1.5 text-xs border ${
                          i === 0 ? "rounded-l-sm" : "rounded-r-sm -ml-px"
                        } ${
                          formData.number === value
                            ? "bg-[#002FA7] text-white border-[#002FA7]"
                            : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"
                        }`}
                        data-testid={`jargon-number-${value}`}
                        title={tr(
                          `Met le terme au ${value} : « ${formData.term || "…"} » → « ${apply(formData.term || "")} »`,
                          `Sets the term to ${value}: “${formData.term || "…"}” → “${apply(formData.term || "")}”`,
                        )}
                      >
                        {label}
                      </button>
                    ))}
                </span>
              </div>
              {formData.lang === "en" && (
                <div className="flex-1 min-w-[220px]">
                  <label className="text-sm font-medium text-zinc-700 mb-1 block">
                    {tr("Équivalent français", "French equivalent")}{" "}
                    <span className="text-zinc-400 font-normal">{tr("(facultatif)", "(optional)")}</span>
                  </label>
                  <Input
                    value={formData.fr_equivalent}
                    onChange={(e) => setFormData({ ...formData, fr_equivalent: e.target.value })}
                    placeholder="Ex: REER"
                    data-testid="jargon-fr-equivalent-input"
                  />
                </div>
              )}
              {/* Tâche 1359 (Eric 2026-06-30) — Le symétrique manquait : un
                  terme français ne pouvait pas porter son équivalent anglais,
                  alors que la quasi-totalité des termes sont créés en français
                  par le pipeline. Le renvoi n'était donc jamais saisissable
                  depuis la fiche qu'on a réellement sous les yeux. */}
              {formData.lang === "fr" && (
                <div className="flex-1 min-w-[220px]">
                  <label className="text-sm font-medium text-zinc-700 mb-1 block">
                    {tr("Équivalent anglais", "English equivalent")}{" "}
                    <span className="text-zinc-400 font-normal">{tr("(facultatif)", "(optional)")}</span>
                  </label>
                  <Input
                    value={formData.en_equivalent}
                    onChange={(e) => setFormData({ ...formData, en_equivalent: e.target.value })}
                    placeholder="Ex: RRSP"
                    data-testid="jargon-en-equivalent-input"
                  />
                </div>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 mb-1 block">
                {tr("Définition", "Definition")}
              </label>
              <textarea
                value={formData.definition}
                onChange={(e) =>
                  setFormData({ ...formData, definition: e.target.value })
                }
                placeholder={tr("Définition du terme...", "Definition of the term...")}
                rows={3}
                required
                className="w-full px-3 py-2 border border-zinc-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] resize-none"
                data-testid="jargon-definition-input"
              />
            </div>
            {/* Vincent #351 (Eric 2026-06-18 SPEC STRICK ULTRA) — Champ "Alias"
                RETIRÉ du formulaire. Les alias migrés apparaissent en tags. */}
            {/* Vincent #31 — Multi-tags hybrides : chips + autocomplete depuis
                les tags déjà utilisés dans l'org. Lowercase + dédup auto. */}
            <div>
              <label className="text-sm font-medium text-zinc-700 mb-1 block">
                Tags <span className="text-zinc-400 font-normal">{tr("(libres — Entrée ou virgule pour ajouter)", "(free — Enter or comma to add)")}</span>
              </label>
              <div
                className="min-h-10 flex flex-wrap items-center gap-1.5 px-2 py-1.5 border border-zinc-200 rounded-sm bg-white focus-within:ring-2 focus-within:ring-[#002FA7]"
                data-testid="jargon-tags-input-wrapper"
                onClick={() => {
                  const el = document.getElementById("jargon-tag-input-field");
                  if (el) el.focus();
                }}
              >
                {(formData.tags || []).map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-teal-50 text-teal-700 border border-teal-200 rounded-sm"
                    data-testid={`tag-chip-${tag}`}
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeTag(tag); }}
                      className="hover:bg-teal-100 rounded-sm p-0.5"
                      data-testid={`remove-tag-${tag}`}
                    >
                      <X size={10} />
                    </button>
                  </span>
                ))}
                <input
                  id="jargon-tag-input-field"
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  onBlur={() => tagInput.trim() && addTag(tagInput)}
                  list="jargon-tag-suggestions"
                  className="flex-1 min-w-[120px] outline-none text-sm border-none bg-transparent"
                  placeholder={(formData.tags || []).length === 0 ? tr("Ex: q1-2026, urgent, fiscalité", "e.g. q1-2026, urgent, taxation") : ""}
                  data-testid="jargon-tag-input"
                />
                <datalist id="jargon-tag-suggestions">
                  {tagSuggestions
                    .filter((s) => !(formData.tags || []).includes(s.tag))
                    .map((s) => (
                      <option key={s.tag} value={s.tag}>{`${s.tag} (${s.count})`}</option>
                    ))}
                </datalist>
              </div>
              {tagSuggestions.length > 0 && (formData.tags || []).length < 5 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  <span className="text-xs text-zinc-400 mr-1">Suggestions :</span>
                  {tagSuggestions
                    .filter((s) => !(formData.tags || []).includes(s.tag))
                    .slice(0, 8)
                    .map((s) => (
                      <button
                        key={s.tag}
                        type="button"
                        onClick={() => addTag(s.tag)}
                        className="px-1.5 py-0.5 text-xs bg-zinc-50 text-zinc-600 border border-zinc-200 rounded-sm hover:border-teal-400 hover:text-teal-700"
                        data-testid={`suggest-tag-${s.tag}`}
                      >
                        + {s.tag}
                      </button>
                    ))}
                </div>
              )}
            </div>
            {/* Tâche 1220 (Eric 2026-06-26) — Qui a rejeté un doublon sur ce
                terme, et quand. Rendu uniquement en modification : sur un
                terme qui n'existe pas encore, il n'y a rien à raconter. */}
            {editingId && <TermAliasTrash termId={editingId} onRestored={() => loadEntries()} />}
            {editingId && <TermRejectionLog termId={editingId} />}
            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={resetForm}
                data-testid="cancel-form-btn"
              >
                {tr("Annuler", "Cancel")}
              </Button>
              <Button
                type="submit"
                className="bg-[#002FA7] hover:bg-[#002280]"
                data-testid="submit-jargon-btn"
              >
                <Check size={16} className="mr-2" />
                {editingId ? "Mettre à jour" : "Ajouter"}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Tâche 1265 (Eric 2026-06-28) — Un mot corrigé deux fois ou plus dans
          les transcriptions est un terme métier qui manque au dictionnaire.
          Le bouton ouvre le formulaire pré-rempli : la définition s'écrit à la
          main, rien n'entre en aveugle. */}
      {isAdmin && (
        <MisspellingCandidates
          onAdd={(term) => {
            resetForm();
            setFormData({
              term,
              definition: "",
              aliases: "",
              categories: ["general"],
              tags: [],
              lang: "fr",
              fr_equivalent: "",
              en_equivalent: "",
              number: "",
            });
            setShowForm(true);
          }}
        />
      )}

      {/* Vincent #232 (2026-06-12 + 2026-06-17 SPEC STRICK) — Catégories
          propres au Dictionnaire (multi-sélection). Le sort/search/filtres ont
          été déplacés dans le bandeau supérieur uniforme (Vincent #231). */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex gap-2 flex-wrap" data-testid="jargon-category-filters">
          {/* Vincent #232 (2026-06-12) — Sélection MULTIPLE de catégories.
              "Toutes" réinitialise ; chaque catégorie se coche/décoche. */}
          {CATEGORIES.map((cat) => {
            const active = cat.value === "all"
              ? selectedCategories.length === 0
              : selectedCategories.includes(cat.value);
            return (
              <button
                key={cat.value}
                onClick={() => {
                  if (cat.value === "all") {
                    setSelectedCategories([]);
                  } else {
                    setSelectedCategories((prev) =>
                      prev.includes(cat.value)
                        ? prev.filter((c) => c !== cat.value)
                        : [...prev, cat.value]
                    );
                  }
                }}
                className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${
                  active
                    ? "bg-[#002FA7] text-white border-[#002FA7]"
                    : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"
                }`}
                data-testid={`category-filter-${cat.value}`}
              >
                {categorieLabel(cat, lang)}
                {cat.value !== "all" && active && <span className="ml-1">✓</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Vincent #31 — Filtre par tag (cliquable). Visible seulement s'il y a
          des tags utilisés. Cliquer sur un tag de chip dans la liste applique
          aussi ce filtre. */}
      {tagSuggestions.length > 0 && (
        <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="jargon-tag-filters">
          <span className="text-xs text-zinc-500 font-medium">Tags :</span>
          <button
            onClick={() => setSelectedTag(null)}
            className={`px-2.5 py-1 text-xs rounded-sm border transition-colors ${
              selectedTag === null
                ? "bg-teal-600 text-white border-teal-600"
                : "bg-white text-zinc-600 border-zinc-200 hover:border-teal-400"
            }`}
            data-testid="tag-filter-all"
          >
            {tr("Tous", "All")}
          </button>
          {tagSuggestions.slice(0, 15).map((s) => (
            <button
              key={s.tag}
              onClick={() => setSelectedTag(selectedTag === s.tag ? null : s.tag)}
              className={`px-2.5 py-1 text-xs rounded-sm border transition-colors ${
                selectedTag === s.tag
                  ? "bg-teal-600 text-white border-teal-600"
                  : "bg-white text-zinc-600 border-zinc-200 hover:border-teal-400"
              }`}
              data-testid={`tag-filter-${s.tag}`}
            >
              {s.tag} <span className="opacity-60">({s.count})</span>
            </button>
          ))}
        </div>
      )}

      {/* Eric (2026-06-14 — LOT K) — Panneau "Candidats détectés" pliable.
          Visible quand showCandidates=true ou auto-déployé après un scan réussi.
          Vincent #335 (Eric 2026-06-17 SPEC STRICK) — Visible UNIQUEMENT pour
          les utilisateurs autorisés à éditer le dictionnaire (isAdmin). */}
      {isAdmin && candidates.length > 0 && (
        <div
          className="bg-amber-50 border border-amber-200 rounded-lg mb-4 overflow-hidden"
          data-testid="candidates-panel"
        >
          <button
            onClick={() => setShowCandidates(!showCandidates)}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-amber-100 transition-colors"
            data-testid="candidates-panel-toggle"
          >
            <span className="flex items-center gap-2 font-semibold text-amber-900">
              <Sparkle size={16} weight="fill" className="text-amber-600" />
              {tr(
                `${candidates.length} candidat${candidates.length > 1 ? "s" : ""} jargon détecté${candidates.length > 1 ? "s" : ""}`,
                `${candidates.length} jargon candidate${candidates.length > 1 ? "s" : ""} detected`,
              )}
              <span className="text-[11px] font-normal text-amber-700">
                — à valider d&apos;un clic
              </span>
            </span>
            <span className="text-amber-600 text-xs font-mono">
              {showCandidates ? "▼" : "▶"}
            </span>
          </button>
          {showCandidates && (
            <>
              {/* Tâche 1176 (Eric 2026-06-25) — Barre de traitement en bloc. */}
              <div
                className="px-4 py-2 flex flex-wrap items-center gap-2 bg-amber-100/70 border-t border-amber-200"
                data-testid="candidates-bulk-bar"
              >
                <label className="flex items-center gap-2 text-xs text-amber-900 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedCandidates.size === candidates.length && candidates.length > 0}
                    onChange={toggleAllCandidates}
                    data-testid="candidates-select-all"
                  />
                  {tr("Tout sélectionner", "Select all")}
                </label>
                <span className="text-xs text-amber-800 tabular-nums" data-testid="candidates-selected-count">
                  {selectedCandidates.size} sélectionné(s)
                </span>
                <div className="ml-auto flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleBulkCandidates("accept")}
                    disabled={selectedCandidates.size === 0 || bulkCandidateBusy}
                    className="bg-teal-600 hover:bg-teal-700 text-white text-xs py-1 px-3 h-7"
                    data-testid="candidates-bulk-accept"
                  >
                    {bulkCandidateBusy
                      ? <SpinnerGap size={12} className="mr-1 animate-spin" />
                      : <Check size={12} className="mr-1" />}
                    Ajouter {selectedCandidates.size || ""}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleBulkCandidates("reject")}
                    disabled={selectedCandidates.size === 0 || bulkCandidateBusy}
                    className="border-rose-300 text-rose-700 hover:bg-rose-50 text-xs py-1 px-3 h-7"
                    data-testid="candidates-bulk-reject"
                  >
                    <X size={12} className="mr-1" />
                    Rejeter {selectedCandidates.size || ""}
                  </Button>
                </div>
              </div>
              <ul className="divide-y divide-amber-100 max-h-[460px] overflow-y-auto">
                {candidates.map((c) => (
                  <li
                    key={c.id}
                    className={`px-4 py-3 flex items-start gap-3 ${selectedCandidates.has(c.id) ? "bg-amber-100" : "hover:bg-amber-25"}`}
                    data-testid={`candidate-${c.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedCandidates.has(c.id)}
                      onChange={() => toggleCandidate(c.id)}
                      className="mt-1.5"
                      data-testid={`candidate-checkbox-${c.id}`}
                      aria-label={tr(`Sélectionner ${c.term}`, `Select ${c.term}`)}
                    />
                    <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-bold text-amber-900" data-testid={`candidate-term-${c.id}`}>
                        {c.term}
                      </span>
                      <span className="px-2 py-0.5 bg-amber-200 text-amber-800 text-[10px] font-bold rounded">
                        {c.occurrences} occ.
                      </span>
                      <span className="px-2 py-0.5 bg-zinc-100 text-zinc-600 text-[10px] rounded">
                        {c.video_count} vidéo(s)
                      </span>
                      {c.ai_enriched && (
                        <span className="px-2 py-0.5 bg-violet-100 text-violet-700 text-[10px] rounded inline-flex items-center gap-1">
                          <Sparkle size={10} weight="fill" /> IA
                        </span>
                      )}
                      {c.proposed_category && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] rounded">
                          {c.proposed_category}
                        </span>
                      )}
                    </div>
                    {c.proposed_definition && (
                      <p className="text-xs text-zinc-700 mt-1" data-testid={`candidate-def-${c.id}`}>
                        <strong className="text-amber-800">{tr("Proposé :", "Proposed:")}</strong> {c.proposed_definition}
                      </p>
                    )}
                    {c.sample_context && (
                      <p className="text-[11px] text-zinc-500 italic mt-1 line-clamp-2">
                        « …{c.sample_context}… »
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <Button
                      size="sm"
                      onClick={() => handleAcceptCandidate(c)}
                      className="bg-teal-600 hover:bg-teal-700 text-white text-xs py-1 px-3 h-7"
                      data-testid={`accept-candidate-${c.id}`}
                    >
                      <Check size={12} className="mr-1" /> {tr("Ajouter", "Add")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRejectCandidate(c)}
                      className="border-zinc-300 text-zinc-600 hover:bg-zinc-50 text-xs py-1 px-3 h-7"
                      data-testid={`reject-candidate-${c.id}`}
                    >
                      <X size={12} className="mr-1" /> Rejeter
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
            </>
          )}
        </div>
      )}

      {/* Entries list */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <SpinnerGap size={32} className="animate-spin text-[#002FA7]" />
        </div>
      ) : filtered.length === 0 ? (
        <div
          className="bg-white border border-zinc-200 rounded-sm p-12 text-center"
          data-testid="jargon-empty-state"
        >
          <BookOpen size={48} className="mx-auto mb-3 text-zinc-300" />
          <h3 className="font-medium text-zinc-600">
            {entries.length === 0
              ? "Dictionnaire vide"
              : "Aucun résultat"}
          </h3>
          <p className="text-sm text-zinc-400 mt-1">
            {entries.length === 0
              ? tr("Commencez par ajouter des termes ou chargez les termes par défaut", "Start by adding terms, or load the default ones")
              : tr("Essayez une autre recherche ou catégorie", "Try another search or category")}
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="jargon-entries-list">
          {/* Tâche 1137 — Barre d'action de la copie en lot, visible dès qu'un
              terme est coché. */}
          {bulkIds.size > 0 && (
            <div
              className="sticky top-2 z-10 flex flex-wrap items-center gap-3 bg-teal-50 border border-teal-200 rounded-sm px-4 py-2.5"
              data-testid="bulk-copy-bar"
            >
              <span className="text-sm font-semibold text-teal-900">
                {bulkIds.size} terme(s) sélectionné(s)
              </span>
              <button
                onClick={() => {
                  setCopyTarget("bulk");
                  setCopyOrgId("");
                }}
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded bg-teal-700 text-white transition-colors hover:bg-teal-800"
                data-testid="bulk-copy-btn"
              >
                <Buildings size={14} />
                {tr("Copier vers une autre organisation", "Copy to another organization")}
              </button>
              <button
                onClick={() => setBulkIds(new Set())}
                className="text-xs text-teal-800 underline transition-colors hover:text-teal-950"
                data-testid="bulk-clear-btn"
              >
                {tr("Tout désélectionner", "Clear selection")}
              </button>
              <button
                onClick={() => setBulkIds(new Set(filtered.map((e) => e.id)))}
                className="text-xs text-teal-800 underline transition-colors hover:text-teal-950"
                data-testid="bulk-select-all-btn"
              >
                Sélectionner les {filtered.length} termes affichés
              </button>
            </div>
          )}
          {filtered.map((entry) => (
            <Fragment key={entry.id}>
            <div
              className="bg-white border border-zinc-200 rounded-sm p-4 hover:border-zinc-300 transition-colors"
              data-testid={`jargon-entry-${entry.id}`}
              data-focus-target={entry.id}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    {/* Tâche 1137 — Sélection pour la copie en lot. Visible
                        seulement quand une autre organisation existe : sinon
                        c'est une case à cocher sans usage. */}
                    {otherOrgs.length > 0 && (
                      <input
                        type="checkbox"
                        checked={bulkIds.has(entry.id)}
                        onChange={() => toggleBulk(entry.id)}
                        className="flex-shrink-0"
                        title={tr("Sélectionner pour une copie en lot", "Select for a bulk copy")}
                        data-testid={`bulk-select-${entry.id}`}
                      />
                    )}
                    {/* Vincent B546 (Eric 2026-07-27) — Coeur favori tout à
                        gauche du terme du dictionnaire. */}
                    <button
                      type="button"
                      onClick={(e) => toggleFavorite(e, entry.id)}
                      className={`p-0.5 rounded transition-colors flex-shrink-0 ${favoriteIds.has(entry.id) ? "text-rose-500" : "text-zinc-300 hover:text-rose-400"}`}
                      data-testid={`fav-btn-${entry.id}`}
                      title={favoriteIds.has(entry.id) ? tr("Retirer des favoris", "Remove from favourites") : tr("Ajouter aux favoris", "Add to favourites")}
                    >
                      <Heart size={16} weight={favoriteIds.has(entry.id) ? "fill" : "regular"} />
                    </button>
                    <h3 className={`font-semibold text-sm ${entry.lang === "en" ? "italic" : ""}`}>
                      <HighlightedText text={entry.term} query={search} testid={`jargon-term-hl-${entry.id}`} />
                    </h3>
                    {/* Tâche 1264 — Un terme anglais est un alias : italique, et
                        renvoi cliquable vers son équivalent français (qui peut
                        rester à compléter). */}
                    {entry.lang === "en" && (
                      <button
                        type="button"
                        onClick={() => {
                          if (entry.fr_equivalent) setSearch(entry.fr_equivalent);
                          else handleEdit(entry);
                        }}
                        className="text-[11px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-900 border border-violet-300 hover:bg-violet-200"
                        data-testid={`jargon-fr-renvoi-${entry.id}`}
                        title={entry.fr_equivalent
                          ? tr(`Terme anglais — voir l'équivalent français « ${entry.fr_equivalent} »`,
                               `English term — see the French equivalent "${entry.fr_equivalent}"`)
                          : tr("Terme anglais sans équivalent français : cliquer pour l'ajouter",
                               "English term with no French equivalent: click to add it")}
                      >
                        {entry.fr_equivalent ? `En → ${entry.fr_equivalent}` : tr("En → équivalent fr ?", "En → fr equivalent?")}
                      </button>
                    )}
                    {/* Tâche 1359 — Renvoi symétrique sur un terme français.
                        Affiché UNIQUEMENT quand l'équivalent est rempli : la
                        quasi-totalité du dictionnaire est en français, une
                        pastille « à compléter » sur chaque ligne deviendrait
                        du bruit qu'on apprend à ignorer. */}
                    {entry.lang !== "en" && entry.en_equivalent && (
                      <button
                        type="button"
                        onClick={() => setSearch(entry.en_equivalent)}
                        className="text-[11px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-900 border border-violet-300 hover:bg-violet-200"
                        data-testid={`jargon-en-renvoi-${entry.id}`}
                        title={tr(`Terme français — voir l'équivalent anglais « ${entry.en_equivalent} »`,
                                  `French term — see the English equivalent "${entry.en_equivalent}"`)}
                      >
                        {`Fr → ${entry.en_equivalent}`}
                      </button>
                    )}
                    {entry.number && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-600 border border-zinc-200"
                        data-testid={`jargon-number-badge-${entry.id}`}
                      >
                        {entry.number}
                      </span>
                    )}
                    {/* Tâche 1163 — Le terme porte la marque du doublon.
                        Bogue 1163 (Éric 2026-06-25) — `duplicate_suspect` est
                        posé par l'ingestion d'une vidéo : la marque apparaît
                        donc même avant toute détection à l'écran. */}
                    {(dupWatch.ids.has(entry.id) || entry.duplicate_suspect) && (
                      <button
                        type="button"
                        onClick={() => setFocusDuplicateEntry(entry)}
                        className="px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-900 border border-amber-300 hover:bg-amber-200 transition-colors"
                        title={lang === "fr" ? "Un terme très proche existe — examiner la fusion" : "A very close term exists — review the merge"}
                        data-testid={`duplicate-badge-${entry.id}`}
                      >
                        {lang === "fr" ? "doublon ?" : "duplicate?"}
                      </button>
                    )}
                    {/* Vincent 2026-06-03 — Affichage de TOUTES les catégories (multi-cat). */}
                    {((entry.categories && entry.categories.length ? entry.categories : [entry.category]).filter(Boolean)).map((c) => (
                      <span key={c} className="px-2 py-0.5 text-xs bg-zinc-100 text-zinc-600 rounded">
                        {(() => {
                          const cat = CATEGORIES.find((x) => x.value === c);
                          return cat ? categorieLabel(cat, lang) : c;
                        })()}
                      </span>
                    ))}
                    {/* Vincent B676-suite (Eric 2026-02-XX) — Badge « Enrichi par
                        N vidéos ». Affiché quand le terme a été consolidé à
                        partir d'une ou plusieurs vidéos via le pipeline
                        `generate_dictionary_terms_from_video` (upsert-on-exists).
                        Le tooltip liste les titres si <=5, ou dit "et N autres". */}
                    {entry.source_video_ids?.length > 0 && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          setSourcePopover({ entryId: entry.id, term: entry.term, rect });
                        }}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-violet-50 text-violet-700 rounded border border-violet-100 hover:bg-violet-100 hover:border-violet-300 transition-colors cursor-pointer"
                        title={
                          lang === "fr"
                            ? `Cliquer pour voir les ${entry.source_video_ids.length} vidéo${entry.source_video_ids.length > 1 ? "s" : ""} qui ont contribué à cette définition.`
                            : `Click to see the ${entry.source_video_ids.length} video${entry.source_video_ids.length > 1 ? "s" : ""} that contributed to this definition.`
                        }
                        data-testid={`enrichment-badge-${entry.id}`}
                      >
                        <Video size={11} weight="bold" />
                        {lang === "fr"
                          ? `Enrichi par ${entry.source_video_ids.length} vidéo${entry.source_video_ids.length > 1 ? "s" : ""}`
                          : `Enriched by ${entry.source_video_ids.length} video${entry.source_video_ids.length > 1 ? "s" : ""}`}
                      </button>
                    )}
                  </div>
                  {/* B1130 (Eric 2026-09-08) — Entrée de renvoi : le mot
                      anglais ne répète PAS la définition (deux définitions à
                      maintenir finiraient par se contredire), il envoie vers
                      la version française qui contient tout. */}
                  {entry.is_cross_reference ? (
                    <p className="text-sm text-zinc-600" data-testid={`jargon-cross-ref-${entry.id}`}>
                      →{" "}
                      <button
                        type="button"
                        onClick={() => {
                          setSearch(entry.see_also_term || "");
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        className="font-semibold text-[#002FA7] underline hover:no-underline"
                        data-testid={`jargon-see-also-${entry.id}`}
                        title={tr(
                          `Voir « ${entry.see_also_term} » — la fiche complète`,
                          `Open “${entry.see_also_term}” — the full entry`,
                        )}
                      >
                        voir « {entry.see_also_term} »
                      </button>
                    </p>
                  ) : (
                  <p className="text-sm text-zinc-600">
                    <HighlightedText text={entry.definition} query={search} testid={`jargon-def-hl-${entry.id}`} />
                  </p>
                  )}
                  {/* Vincent #351 (Eric 2026-06-18 SPEC STRICK ULTRA) — Bloc
                      d'affichage des Alias RETIRÉ de la tuile. Les alias migrés
                      apparaissent désormais comme tags ci-dessous. */}
                  {/* Vincent #31 — Affichage des tags + clic pour filtrer. */}
                  {entry.tags?.length > 0 && (
                    <div className="flex items-center gap-1 mt-2 flex-wrap" data-testid={`entry-tags-${entry.id}`}>
                      <span className="text-[10px] text-zinc-400 uppercase tracking-wide mr-0.5">#</span>
                      {/* Vincent XLSX 2026-07-09 #11 — tri A→Z insensible à la casse */}
                      {[...entry.tags].sort((a, b) => String(a).toLowerCase().localeCompare(String(b).toLowerCase(), "fr", { sensitivity: "base" })).map((tag) => (
                        <button
                          key={tag}
                          onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                          className={`px-1.5 py-0.5 text-xs rounded border transition-colors ${
                            selectedTag === tag
                              ? "bg-teal-600 text-white border-teal-600"
                              : "bg-teal-50 text-teal-700 border-teal-100 hover:border-teal-400"
                          }`}
                          data-testid={`entry-tag-${entry.id}-${tag}`}
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {isAdmin && (
                  <div className="flex gap-1 flex-shrink-0">
                    {/* Vincent #228 (Eric 2026-02-06) — Boutons strictement alignés
                        sur la KB modal Q&R/Scripts : MÊMES couleurs, MÊMES icônes,
                        MÊME ordre (Accepter > Refuser > Éditer > Historique >
                        Brouillon > Supprimer). Refs : KnowledgePage.js:1545-1640. */}
                    {(entry.status === "pending_review" || entry.status === "draft" || !entry.status) && (
                      <button
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/api/jargon/${entry.id}/review`, { action: "approve" }, { withCredentials: true });
                            toast.success(lang === "fr" ? "Terme validé" : "Term approved");
                            loadEntries();
                          } catch (e) { toast.error(e.response?.data?.detail || tr("Erreur", "Error")); }
                        }}
                        className="px-2.5 py-1.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white inline-flex items-center text-xs font-semibold"
                        title={lang === "fr" ? "Accepter" : "Approve"}
                        data-testid={`approve-jargon-${entry.id}`}
                      >
                        <Check size={14} weight="bold" />
                      </button>
                    )}
                    {entry.status === "pending_review" && (
                      <button
                        onClick={async () => {
                          const ok = await confirmDialog(
                            lang === "fr" ? "Refuser ce terme ? Il sera masqué mais conservé pour audit (Loi 25)." : "Reject this term? It will be hidden but kept for audit (Law 25).",
                            { danger: true, confirmLabel: lang === "fr" ? "Refuser" : "Reject" },
                          );
                          if (!ok) return;
                          try {
                            await axios.put(`${API}/api/jargon/${entry.id}/review`, { action: "reject" }, { withCredentials: true });
                            toast.success(lang === "fr" ? "Terme refusé" : "Term rejected");
                            loadEntries();
                          } catch (e) { toast.error(e.response?.data?.detail || tr("Erreur", "Error")); }
                        }}
                        className="px-2.5 py-1.5 rounded-lg border border-rose-300 text-rose-700 hover:bg-rose-50 inline-flex items-center text-xs font-semibold"
                        title={lang === "fr" ? "Refuser" : "Reject"}
                        data-testid={`reject-jargon-${entry.id}`}
                      >
                        <X size={14} weight="bold" />
                      </button>
                    )}
                    <button
                      onClick={() => handleEdit(entry)}
                      className="p-2 hover:bg-secondary rounded-lg text-[#002FA7] inline-flex items-center"
                      title={lang === "fr" ? "Éditer" : "Edit"}
                      data-testid={`edit-jargon-${entry.id}`}
                    >
                      <Pencil size={16} />
                    </button>
                    {/* Tâche 1160 (Eric 2026-06-17) — Corriger les majuscules
                        en un clic : le dictionnaire a hérité de centaines de
                        titres en Title Case (« Actifs Sous Gestion »), et les
                        reprendre à la main est un travail de copiste. */}
                    <button
                      onClick={() => fixCase(entry)}
                      disabled={fixingCase === entry.id}
                      className="p-2 hover:bg-secondary rounded-lg text-violet-700 inline-flex items-center disabled:opacity-40"
                      title={lang === "fr"
                        ? "Corriger les majuscules de ce terme (IA)"
                        : "Fix capitalisation (AI)"}
                      data-testid={`fix-case-jargon-${entry.id}`}
                    >
                      <TextAa size={16} />
                    </button>
                    {/* Tâche 1353 (Eric 2026-06-30) — « Reformuler » : les
                        enrichissements par document empilent les précisions à
                        la suite ; au bout de quatre documents la définition
                        redit trois fois la même chose. L'IA réécrit une
                        définition unifiée — les sources cliquables du bouton
                        bleu ne sont jamais touchées. */}
                    <button
                      onClick={() => reformuler(entry)}
                      disabled={reformulating === entry.id}
                      className="p-2 hover:bg-secondary rounded-lg text-violet-700 inline-flex items-center disabled:opacity-40"
                      title={lang === "fr"
                        ? "Reformuler la définition : l'IA réécrit une définition unifiée, sans redite, et signale ce qui appartient à un autre terme (les sources sont conservées)"
                        : "Reformulate the definition: the AI rewrites one unified definition, without repetition, and flags what belongs to another term (sources are kept)"}
                      data-testid={`reformulate-jargon-${entry.id}`}
                    >
                      {reformulating === entry.id
                        ? <Sparkle size={16} className="animate-pulse" />
                        : <MagicWand size={16} />}
                    </button>
                    <button
                      onClick={() => openHistory(entry)}
                      className="p-2 hover:bg-secondary rounded-lg text-amber-700 inline-flex items-center"
                      title={lang === "fr" ? "Historique des versions" : "History"}
                      data-testid={`history-jargon-${entry.id}`}
                    >
                      <ClockCounterClockwise size={16} />
                    </button>
                    {/* Vincent #266 batch FLHQ (Eric 2026-02-06) — Bouton "Vidéos
                        d'apparition" : ouvre modal listant les vidéos où le terme
                        apparaît, avec extraits de contexte. */}
                    <button
                      onClick={() => openUsage(entry)}
                      className="p-2 hover:bg-secondary rounded-lg text-blue-700 inline-flex items-center"
                      title={lang === "fr" ? "Voir les vidéos où ce terme apparaît" : "View videos where this term appears"}
                      data-testid={`usage-jargon-${entry.id}`}
                    >
                      <Video size={16} />
                    </button>
                    {/* Tâche 1133 (Eric 2026-06-09) — Le cloisonnement laisse
                        les autres organisations avec un dictionnaire réduit :
                        ce bouton le repeuple terme par terme, plutôt que de
                        rouvrir une visibilité globale. */}
                    {otherOrgs.length > 0 && (
                      <button
                        onClick={() => openCopyDialog(entry)}
                        className="p-2 hover:bg-secondary rounded-lg text-teal-700 inline-flex items-center"
                        title={lang === "fr"
                          ? "Copier ce terme vers une autre organisation"
                          : "Copy this term to another organisation"}
                        data-testid={`copy-org-jargon-${entry.id}`}
                      >
                        <Buildings size={16} />
                      </button>
                    )}
                    {entry.status === "approved" && (
                      <button
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/api/jargon/${entry.id}/review`, { action: "draft" }, { withCredentials: true });
                            toast.success(lang === "fr" ? "Passé en brouillon" : "Set to draft");
                            loadEntries();
                          } catch (e) { toast.error(e.response?.data?.detail || tr("Erreur", "Error")); }
                        }}
                        className="p-2 hover:bg-amber-50 rounded-lg text-amber-600 inline-flex items-center"
                        title={lang === "fr" ? "Brouillon" : "Draft"}
                        data-testid={`draft-jargon-${entry.id}`}
                      >
                        <ArrowsClockwise size={16} />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(entry.id)}
                      className="p-2 hover:bg-red-50 rounded-lg text-red-500 inline-flex items-center"
                      title={lang === "fr" ? "Supprimer" : "Delete"}
                      data-testid={`delete-jargon-${entry.id}`}
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>
            {/* B1089 — Noeud d'accueil du formulaire d'édition, rendu par
                React sous la ligne éditée. Le formulaire y est téléporté. */}
            {showForm && isAdmin && editingId === entry.id && (
              <div
                ref={setEditHost}
                data-testid={`jargon-edit-host-${entry.id}`}
              />
            )}
            </Fragment>
          ))}
        </div>
      )}

      {/* Stats */}
      {entries.length > 0 && (
        <div className="mt-6 text-xs text-zinc-400 text-center" data-testid="jargon-stats">
          {entries.length} terme{entries.length > 1 ? "s" : ""} au total
          {search && ` - ${filtered.length} resultat${filtered.length > 1 ? "s" : ""}`}
        </div>
      )}

      {/* Vincent #32 — Modal Historique des versions */}
      {historyEntry && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={closeHistory}
          data-testid="jargon-history-modal"
        >
          <div
            className="bg-white rounded-sm shadow-xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-zinc-200">
              <div>
                <h3 className="font-semibold text-base font-['Outfit']" data-testid="history-modal-title">
                  Historique — <span className="text-[#002FA7]">{historyEntry.term}</span>
                </h3>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Traçabilité Loi 25 · {historyVersions.length} version{historyVersions.length > 1 ? "s" : ""}
                </p>
              </div>
              <button
                onClick={closeHistory}
                className="p-1.5 hover:bg-zinc-100 rounded"
                data-testid="close-history-btn"
              >
                <X size={20} />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-5">
              {historyLoading ? (
                <div className="flex items-center justify-center h-32">
                  <SpinnerGap size={28} className="animate-spin text-[#002FA7]" />
                </div>
              ) : historyVersions.length === 0 ? (
                <div className="text-center text-sm text-zinc-500 py-8">
                  {tr("Aucune version trouvée.", "No version found.")}
                </div>
              ) : (
                <div className="space-y-3" data-testid="history-versions-list">
                  {historyVersions.map((v, idx) => {
                    const isLatest = idx === 0;
                    const date = v.changed_at ? new Date(v.changed_at).toLocaleString("fr-CA", {
                      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit"
                    }) : "—";
                    const opColors = {
                      create: "bg-teal-50 text-teal-700 border-teal-200",
                      update: "bg-blue-50 text-blue-700 border-blue-200",
                      delete: "bg-red-50 text-red-700 border-red-200",
                      restore: "bg-amber-50 text-amber-700 border-amber-200",
                    };
                    const opLabels = { create: "Création", update: "Modification", delete: "Suppression", restore: "Restauration" };
                    return (
                      <div
                        key={v.id}
                        className={`border rounded-sm p-3 ${isLatest ? "border-[#002FA7] bg-blue-50/20" : "border-zinc-200 bg-white"}`}
                        data-testid={`history-version-${v.version}`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold">v{v.version}</span>
                            <span className={`px-1.5 py-0.5 text-[10px] uppercase tracking-wide border rounded ${opColors[v.operation] || "bg-zinc-50 text-zinc-600 border-zinc-200"}`}>
                              {opLabels[v.operation] || v.operation}
                            </span>
                            {isLatest && (
                              <span className="px-1.5 py-0.5 text-[10px] uppercase tracking-wide bg-[#002FA7] text-white rounded">
                                Actuelle
                              </span>
                            )}
                          </div>
                          {!isLatest && isAdmin && (
                            <button
                              onClick={() => handleRestore(v.version)}
                              className="text-xs text-[#002FA7] hover:bg-blue-50 px-2 py-1 rounded border border-[#002FA7]"
                              data-testid={`restore-version-${v.version}`}
                            >
                              <ArrowsClockwise size={12} className="inline mr-1" />
                              Restaurer
                            </button>
                          )}
                        </div>
                        <div className="text-xs text-zinc-500 mb-2">
                          {v.changed_by_name || v.changed_by_email || "—"} · {date}
                        </div>
                        {v.diff && Object.keys(v.diff).length > 0 && (
                          <div className="space-y-1.5 mt-2 pt-2 border-t border-zinc-100">
                            {Object.entries(v.diff).map(([field, change]) => (
                              <div key={field} className="text-xs">
                                <span className="font-medium text-zinc-700 capitalize">{field}</span>
                                <div className="mt-0.5 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                  <div className="bg-red-50 border border-red-100 rounded px-2 py-1 text-red-800 break-words">
                                    <span className="text-[10px] uppercase text-red-500 mr-1">Avant</span>
                                    {Array.isArray(change.before) ? change.before.join(", ") : (change.before === null || change.before === undefined ? <em className="text-zinc-400">vide</em> : String(change.before))}
                                  </div>
                                  <div className="bg-teal-50 border border-teal-100 rounded px-2 py-1 text-teal-800 break-words">
                                    <span className="text-[10px] uppercase text-teal-600 mr-1">{tr("Après", "After")}</span>
                                    {Array.isArray(change.after) ? change.after.join(", ") : (change.after === null || change.after === undefined ? <em className="text-zinc-400">vide</em> : String(change.after))}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Vincent #266 batch FLHQ (Eric 2026-02-06) — Modal "Vidéos d'apparition"
          du terme courant. Affiche le nombre de vidéos, le total d'occurrences,
          et pour chaque vidéo : titre, count, et jusqu'à 3 extraits de contexte
          avec le terme surligné en jaune. */}
      {usageEntry && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeUsage} data-testid="usage-overlay">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="usage-modal">
            <div className="px-5 py-4 border-b border-blue-200 bg-blue-50 flex items-start justify-between gap-3">
              <div>
                <h2 className="font-bold text-lg text-blue-900 flex items-center gap-2">
                  <Video size={20} /> {lang === "fr" ? "Vidéos d'apparition" : "Videos where this term appears"}
                </h2>
                <p className="text-xs text-blue-700 mt-1">
                  {lang === "fr" ? "Terme : " : "Term: "}<span className="font-mono font-semibold">{usageEntry.term}</span>
                  {usageData?.aliases_searched?.length > 0 && (
                    <> · {lang === "fr" ? "Alias : " : "Aliases: "}<span className="font-mono">{[...usageData.aliases_searched].sort((a, b) => String(a).toLowerCase().localeCompare(String(b).toLowerCase(), "fr", { sensitivity: "base" })).join(", ")}</span></>
                  )}
                </p>
              </div>
              <button onClick={closeUsage} className="p-1 rounded hover:bg-blue-100" data-testid="close-usage-btn"><X size={20} /></button>
            </div>
            <div className="px-5 py-3 bg-zinc-50 border-b border-zinc-200 text-xs text-zinc-700 flex items-center gap-4">
              <span>📹 <strong>{usageData?.total_videos || 0}</strong> {lang === "fr" ? "vidéo(s)" : "video(s)"}</span>
              <span>🔢 <strong>{usageData?.total_occurrences || 0}</strong> {lang === "fr" ? "occurrence(s) totale(s)" : "total occurrence(s)"}</span>
            </div>
            <div className="overflow-y-auto p-5 space-y-4 flex-1">
              {usageLoading ? (
                <div className="flex items-center justify-center py-12">
                  <SpinnerGap size={32} className="animate-spin text-blue-600" />
                </div>
              ) : !usageData || usageData.videos.length === 0 ? (
                <div className="text-center py-12 text-zinc-400">
                  <Video size={48} className="mx-auto mb-3 opacity-30" />
                  <p>{lang === "fr" ? "Aucune vidéo ne contient ce terme." : "No video contains this term."}</p>
                  <p className="text-xs mt-2">{lang === "fr" ? "Vérifie que les vidéos ont bien été transcrites." : "Check that videos have been transcribed."}</p>
                </div>
              ) : (
                usageData.videos.map((v) => (
                  <div key={v.video_id} className="border border-zinc-200 rounded-lg overflow-hidden" data-testid={`usage-video-${v.video_id}`}>
                    <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{v.video_title || (lang === "fr" ? "Sans titre" : "Untitled")}</div>
                        <div className="text-[10px] text-zinc-500">{lang === "fr" ? "Occurrences : " : "Occurrences: "}<strong>{v.occurrence_count}</strong></div>
                      </div>
                      <a
                        href={`/dashboard/video/${v.video_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1 text-xs rounded-lg border border-blue-300 text-blue-700 hover:bg-blue-50 inline-flex items-center gap-1"
                        data-testid={`usage-open-video-${v.video_id}`}
                      >
                        {lang === "fr" ? "Ouvrir →" : "Open →"}
                      </a>
                    </div>
                    <div className="p-3 space-y-2 bg-white">
                      {v.extracts.map((ex, idx) => {
                        // Surligne le mot matché en jaune
                        const before = ex.text.substring(0, ex.match_start);
                        const matched = ex.text.substring(ex.match_start, ex.match_end);
                        const after = ex.text.substring(ex.match_end);
                        return (
                          <div key={idx} className="text-xs leading-relaxed bg-zinc-50 border border-zinc-100 rounded p-2 italic">
                            <span className="text-zinc-700">{before}</span>
                            <mark className="bg-yellow-300 text-zinc-900 font-semibold not-italic px-0.5 rounded">{matched}</mark>
                            <span className="text-zinc-700">{after}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
      {/* Vincent B677 (Eric 2026-02-XX) — Popover cliquable sur badge « Enrichi par N vidéos » */}
      {sourcePopover && (
        <SourceVideosPopover
          entryId={sourcePopover.entryId}
          term={sourcePopover.term}
          anchorRect={sourcePopover.rect}
          onClose={() => setSourcePopover(null)}
        />
      )}

      {/* Vincent B677 (Eric 2026-02-XX) — Modale détection/fusion de doublons */}
      {showDuplicatesModal && (
        <DuplicateReviewModal
          onClose={() => setShowDuplicatesModal(false)}
          onMerged={() => loadEntries()}
        />
      )}

      {/* Tâche 1172 — Fusion d'une sélection manuelle, dans la même modale
          que la fusion guidée (définitions côte à côte, synthèse IA). */}
      {showPendingModal && (
        <PendingTermsModal
          lang={lang}
          entries={entries || []}
          onClose={() => setShowPendingModal(false)}
          onChanged={() => {
            refreshPendingCount();
            loadEntries();
            refreshDuplicateWatch();
          }}
        />
      )}

      {/* Bogue 1198 — Sous-liste : les doublons du seul terme cliqué. La liste
          complète reste accessible par le bandeau jaune. */}
      {focusDuplicateEntry && (
        <DuplicateReviewModal
          focusEntry={focusDuplicateEntry}
          onClose={() => setFocusDuplicateEntry(null)}
          onMerged={() => {
            setFocusDuplicateEntry(null);
            loadEntries();
            refreshDuplicateWatch();
          }}
        />
      )}

      {manualMergeGroup && (
        <DuplicateReviewModal
          manualGroup={manualMergeGroup}
          onClose={() => setManualMergeGroup(null)}
          onMerged={() => {
            setManualMergeGroup(null);
            setBulkIds(new Set());
            loadEntries();
          }}
        />
      )}

      {/* Tâche 1161 — Majuscules en masse (aperçu, cases à cocher). */}
      <BulkCaseFixModal
        open={showBulkCaseModal}
        onClose={() => setShowBulkCaseModal(false)}
        onApplied={() => loadEntries()}
      />

      {/* Vincent B961 (Eric 2026-02-14) — Modale cleanup blacklist B960.d */}
      <DictCleanupModal
        open={showCleanupModal}
        onClose={() => setShowCleanupModal(false)}
        onCleaned={() => loadEntries()}
        lang={lang}
      />

      {/* Tâche 1219 — Nettoyer les alias (désactivation réversible) */}
      <AliasCleanupModal
        open={showAliasModal}
        onClose={() => setShowAliasModal(false)}
        onChanged={() => loadEntries()}
      />


      {/* Tâche 1131 — Modale des renvois EN/FR manquants */}
      <CrossRefBackfillModal
        open={showCrossRefModal}
        onClose={() => setShowCrossRefModal(false)}
        onGenerated={() => loadEntries()}
        lang={lang}
      />

      {/* Tâche 1133 — Choix de l'organisation de destination. Un choix
          silencieux de la première organisation copierait au mauvais endroit
          sans que personne s'en aperçoive. */}
      {copyTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          data-testid="copy-org-dialog"
        >
          <div className="w-full max-w-md bg-white rounded-lg shadow-2xl p-5">
            <h3 className="text-base font-semibold text-zinc-900 mb-1">
              {copyTarget === "bulk"
                ? `Copier ${bulkIds.size} terme(s)`
                : `Copier « ${copyTarget.term} »`}
            </h3>
            <p className="text-xs text-zinc-600 mb-4">
              {copyTarget === "bulk"
                ? tr("Des copies indépendantes seront créées. Les termes déjà présents dans la destination sont simplement ignorés, et les liens vers vos vidéos ne sont pas copiés.", "Independent copies will be created. Terms already present in the destination are simply skipped, and links to your videos are not copied.")
                : tr("Une copie indépendante sera créée : la modifier là-bas n'affectera pas votre fiche, et les liens vers vos vidéos ne sont pas copiés.", "An independent copy will be created: editing it there will not affect your entry, and links to your videos are not copied.")}
            </p>
            <label className="block text-xs font-semibold text-zinc-700 mb-1.5">
              {tr("Organisation de destination", "Destination organization")}
            </label>
            <select
              value={copyOrgId}
              onChange={(e) => setCopyOrgId(e.target.value)}
              className="w-full h-9 px-3 border border-zinc-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              data-testid="copy-org-select"
            >
              <option value="">{tr("Choisir une organisation…", "Choose an organization…")}</option>
              {otherOrgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
            {copyOrgId && (
              <p className="mt-2 text-xs text-teal-800" data-testid="copy-org-target-recap">
                Destination : <strong>{otherOrgs.find((o) => o.id === copyOrgId)?.name}</strong>
              </p>
            )}
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setCopyTarget(null)}
                disabled={copying}
                className="text-xs px-3 py-1.5 rounded border border-zinc-300 bg-white transition-colors hover:bg-zinc-50 disabled:opacity-50"
                data-testid="copy-org-cancel"
              >
                {tr("Annuler", "Cancel")}
              </button>
              <button
                onClick={confirmCopy}
                disabled={copying || !copyOrgId}
                className="text-xs font-semibold px-3 py-1.5 rounded bg-teal-700 text-white transition-colors hover:bg-teal-800 disabled:opacity-50"
                data-testid="copy-org-confirm"
              >
                {copying ? "Copie…" : "Copier"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tâche 1135 — Modale des équivalences FR/EN */}
      <TranslationPairsModal
        open={showPairsModal}
        onClose={() => setShowPairsModal(false)}
        onChanged={() => loadTranslationPairs()}
        canEdit={isAdmin}
      />

      {/* Tâche 1353 — Résultat d'une reformulation : avant/après et
          arbitrage des fragments à déplacer. */}
      <ReformulationResultModal
        open={!!reformResult}
        onClose={() => setReformResult(null)}
        result={reformResult}
        lang={lang}
        onApplied={() => loadEntries()}
      />

      {/* Vincent B1011 (Eric 2026-02-26 SPEC STRICK PROD) — Modale
          import CSV massif de termes du dictionnaire. */}
      <CsvImportModal
        open={showJargonImportModal}
        onClose={() => setShowJargonImportModal(false)}
        title={lang === "fr" ? "Importer un CSV dans le dictionnaire" : "Import CSV into dictionary"}
        description={
          <div>
            <p className="mb-1">
              {lang === "fr"
                ? "Format attendu : colonne obligatoire "
                : "Expected format: required column "}
              <code className="bg-zinc-100 px-1 rounded">term</code>
              {lang === "fr" ? ", optionnelles " : ", optional "}
              <code className="bg-zinc-100 px-1 rounded">definition</code>
              , <code className="bg-zinc-100 px-1 rounded">aliases</code>
              , <code className="bg-zinc-100 px-1 rounded">categories</code>.
            </p>
            <p className="text-xs text-zinc-500">
              {lang === "fr"
                ? "Les aliases et categories multiples sont séparés par le caractère « | » dans une cellule. Les termes déjà existants (comparaison insensible à la casse) sont ignorés."
                : "Multiple aliases and categories are separated by \"|\" within a cell. Existing terms (case-insensitive match) are skipped."}
            </p>
          </div>
        }
        templateCsv={"term,definition,aliases,categories\nREER,Régime enregistré d'épargne-retraite,RER|R.E.E.R,retraite|fiscalité\nCELI,Compte d'épargne libre d'impôt,,épargne\n"} // i18n-donnee: contenu d'un fichier CSV modèle, pas du texte d'écran — les en-têtes sont les clés attendues par l'importateur et les deux lignes sont des exemples de données québécoises
        templateFilename="modele-dictionnaire.csv"
        onSubmit={submitJargonImport}
        resultRenderer={(r) => (
          <div>
            <p className="font-semibold">
              {lang === "fr" ? "Import terminé." : "Import complete."}
            </p>
            <ul className="text-xs mt-1 space-y-0.5">
              <li>{lang === "fr" ? "Ajoutés" : "Added"} : <b>{r.added ?? 0}</b></li>
              <li>{lang === "fr" ? "Doublons ignorés" : "Duplicates skipped"} : <b>{r.skipped_duplicates ?? 0}</b></li>
              <li>{lang === "fr" ? "Invalides" : "Invalid"} : <b>{r.skipped_invalid ?? 0}</b></li>
              {/* Tâche 1228 — Dire pourquoi un alias n'est pas entré, sinon la
                  personne recommence au fichier suivant. */}
              {(r.skipped_self_aliases ?? 0) > 0 && (
                <li className="text-amber-800" data-testid="jargon-import-self-aliases">
                  {lang === "fr"
                    ? "Alias retirés car identiques au titre de leur terme"
                    : "Aliases dropped (identical to their own term)"}{" "}
                  : <b>{r.skipped_self_aliases}</b>
                </li>
              )}
              <li>{lang === "fr" ? "Lignes traitées" : "Rows processed"} : <b>{r.processed ?? 0}</b></li>
            </ul>
          </div>
        )}
        dataTestidPrefix="jargon-csv-import"
        lang={lang}
      />

      {/* Vincent B1012 (Eric 2026-02-26 SPEC STRICK PROD) — Modale
          historique + rollback des imports (jargon). */}
      <ImportHistoryModal
        open={showImportHistoryModal}
        onClose={() => setShowImportHistoryModal(false)}
        type="jargon"
        onRolledBack={() => loadEntries()}
        dataTestidPrefix="jargon-import-history"
        lang={lang}
      />
    </div>
  );
}
